/**
 * Ordens (DEMO-03, telas/Ordens.tsx, DocumentosOperacionais.tsx e o PDF da OCF),
 * persistentes (B5):
 *  - Ordem de Compra ao Fornecedor (OCF-, só Venda): estados Backlog, Faturada
 *    parcialmente, Faturada, Faturada parcialmente · saldo cancelado e Cancelada
 *    (CALCULADOS pelos faturamentos); Emitida/enviada e Confirmada são MARCOS;
 *    "Substituída" é RÓTULO. Uma entidade faturadora por Ordem.
 *  - Fluxo: preenchida do Pedido congelado → CONFERÊNCIA (só dados operacionais)
 *    → PDF → emissão/envio → faturamentos (evento) → estorno 🔒 / cancelar 🔒.
 *  - OL (Locação) e OTI (Trade-in): nomes e prefixos PROVISÓRIOS; dados do
 *    Pedido congelado; em conferência → emitida; cancelamento 🔒.
 */
import { useState, type ReactNode } from 'react';
import { api } from '../api.ts';
import { navegar } from '../rotas.ts';
import { useSessao } from '../sessao.tsx';
import { useRotuloTrilha } from '../componentes/trilha.tsx';
import { buscar, dataBr, intervalo, noPeriodo, periodoInicial, textoMotivo, type Chave, type Tipo } from '../../../backend/src/compartilhado/oportunidades.ts';
import { economia, enderecoTexto, PAGADORES, pagamentoTexto, regraComissao, type ProdutoCopia } from '../../../backend/src/compartilhado/propostas.ts';
import { textoReajuste } from '../../../backend/src/compartilhado/comercial.ts';
import { ESTADOS_ORDEM, motivoExigeObservacao, NOME_DOCUMENTO, type EstadoOrdem, type ItemOrdem, type SituacaoDocumento } from '../../../backend/src/compartilhado/ordens.ts';
import type { ConteudoPedido } from '../../../backend/src/compartilhado/pedidos.ts';
import { apurarOrdem, margem as margemDe, percentual as percentualDe, somar, ZERO, type OrdemConc, type Valores } from '../../../backend/src/compartilhado/conciliacao.ts';
import { Ajuda, CampoBusca, CardLista, CardsLista, ChipsStatus, Estado, EstadoCarga, Modal, Nota, ResumoIndicadores, RolagemIndicada, SeloSituacao, Th, useCarga, useOrdenacao, type Tom } from './ui.tsx';
import { usePersonalizacao } from './personalizacao.tsx';
import { BarraFiltros, FiltroMultiplo, filtroPeriodo, filtroSelecao, SeloTipo, usePeriodo } from './filtros.tsx';
import { CampoData, CampoDataHora, hojeIso } from './CampoData.tsx';
import { CabecalhoOrdem, FaixaCondicoes, Junta, LinhaSecao, Par, Passos, QuadroCampos, QuadroLocal, Recolhivel, TextoHistorico, textoHistorico, Empilha, type Etapa } from './FichaOrdem.tsx';
import { Campos, FolhaDocumento, Secao } from './proposta/Documentos.tsx';
import { Bloco, DocumentoInttrix, Icone, Linhas, linhasEndereco, mesmoEndereco, Pares } from './documentos/DocumentoInttrix.tsx';
import { moeda, moeda2, pct, pctNum } from './proposta/formatos.ts';
import { BotaoExcluir, depoisDeExcluir } from './Lixeira.tsx';
import { FichaOl } from './FichaOl.tsx';
import { OlPdf } from './OlPdf.tsx';

const NOTA_FORNECEDOR = 'Parceiro comercial = o grupo; Entidade faturadora = o CNPJ específico. A OCF mantém “Fornecedor” no nome porque descreve o destinatário da compra.';
const TOM_ORDEM: Record<EstadoOrdem, Tom> = { backlog: 'backlog', faturada_parcialmente: 'parcial', faturada: 'positivo', faturada_saldo_cancelado: 'encerrado', cancelada: 'encerrado' };
export const tomOrdem = (e: EstadoOrdem) => TOM_ORDEM[e];
const tomDocumento = (s: SituacaoDocumento): Tom => (s === 'emitida' ? 'positivo' : s === 'cancelada' ? 'encerrado' : 'backlog');
const agora = () => new Date().toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 16).replace(' ', 'T');
const quando = (dh: string) => `${dataBr(dh.slice(0, 10))} ${dh.slice(11, 16)}`;

export interface EntidadeOrdem {
  id: string;
  grupoId: string;
  grupo: string;
  papeis: string[];
  razao: string;
  fantasia: string;
  cnpj: string;
  uf: string;
  codigoRevenda: string;
  contato: string;
  email: string;
  telefone: string;
}
export interface OcfResumo {
  id: string;
  numero: string;
  tipo: 'venda';
  situacao: EstadoOrdem;
  pedido: { id: string; numero: string; tipo: Tipo };
  cliente: { id: string; nome: string; razao?: string; cnpj?: string };
  entidade: EntidadeOrdem;
  dataCriacao: string;
  dataFaturamento: string | null;
  /** marco "emitida e enviada ao fornecedor" registrado */
  emitidaFornecedor?: boolean;
  itens: (ItemOrdem & { faturada: number })[];
  progresso: { total: number; faturada: number };
  valores: { custo: number; venda: number; margem: number; faturado: number; saldo: number };
  substituta: string | null;
  origem: string | null;
  chaves: Chave[];
  /** P3-34 a P3-38 (D4): base da conciliação (revisões, cancelamento e lançamentos com a sua base) */
  conc?: OrdemConc;
  revisao?: number;
}
export interface DocResumo {
  id: string;
  numero: string;
  tipo: 'locacao' | 'trade_in';
  situacao: SituacaoDocumento;
  situacaoTexto: string;
  pedido: { id: string; numero: string; tipo: Tipo };
  cliente: { id: string; nome: string; razao?: string; cnpj?: string };
  entidade: EntidadeOrdem | null;
  dataCriacao: string;
  valores: { valorCliente: number; comissao: number; custo: number; venda: number };
  chaves: Chave[];
  /** P3-39: OL — contrato, etapa e próxima ação do acompanhamento; apuração (pagamentos e recebimentos válidos) */
  locacao?: { mensalidade: number; prazoMeses: number; periodicidade: string; percentual: number; margemParcela: number; etapa: string; tomEtapa: string; proximaAcao: { descricao: string; data: string; responsavel: string } | null; pendencia: string | null; recebimentos: { data: string; valor: number }[] | null; pagamentos: { data: string; valor: number }[] | null } | null;
}
/** P4 bloco 3 (D2/D3): valor do documento — congelado no aceite ou reconstituído com evidência pela auditoria. */
export interface ValorHistorico {
  estado: 'informada' | 'nao_informada' | 'sem_evidencia' | 'nao_se_aplica';
  valor: string;
  fonte: 'pedido' | 'auditoria' | null;
  situacao?: string;
  atual?: string;
}
export interface CadastroDoPedido {
  dataReferencia: string;
  faturamento: { ie: ValorHistorico; email: ValorHistorico };
  entrega: { ie: ValorHistorico };
}
export interface PedidoDaOrdem {
  id: string;
  numero: string;
  tipo: Tipo;
  versao: number;
  conteudo: ConteudoPedido;
  cliente: { id: string; nome: string; codigo: string; ie: string; razao: string; cnpj: string };
  contatoPrincipal: { nome: string; email: string; telefone: string } | null;
  oportunidade: { id: string; numero: string; titulo: string };
  proposta: { id: string; numero: string };
  responsavel: { nome: string; email: string };
  ocsVigentes: { numero: string; tipo: string; dataRecebimento: string | null }[];
  cadastro: CadastroDoPedido;
}
export interface Marco {
  dataHora: string;
  autor: string;
}
export interface Lancamento {
  id: string;
  nf: string;
  dataHora: string;
  itens: { itemId: string; quantidade: number }[];
  valor: number;
  observacao: string;
  autor: string;
  estorno: { dataHora: string; autor: string; justificativa: string } | null;
}
export interface Historico {
  dataHora: string;
  texto: string;
  autor: string;
}
export interface OcfCompleta extends OcfResumo {
  rotuloEntidade?: 'Entidade faturadora' | 'Entidade faturada';
  pedidoCompleto: PedidoDaOrdem;
  frete: { modalidade: 'CIF' | 'FOB'; pagador: 'incluso' | 'cliente' | 'inttrix'; valor: number };
  freteTotalPedido: number;
  observacoes: string;
  contatoEntrega: string;
  telefoneEntrega: string;
  /** P4 bloco 3 (D1): emissão, envio registrado e confirmação do fornecedor são registros distintos */
  emitida: Marco | null;
  enviada: (Marco & { registroAnterior: boolean; envio: { dataHora: string; meio: 'email' | 'portal' | 'outro'; destinatario: string; observacao: string; evidencia: { id: string; nome: string } | null } | null }) | null;
  confirmada: (Marco & { data: string | null; observacao: string; pedidoFornecedor: string; alteracoesPedidoFornecedor: { de: string; para: string; dataHora: string; autor: string }[] }) | null;
  cancelamento: (Marco & { motivo: string; observacao: string; saldoCancelado: number }) | null;
  faturamentos: Lancamento[];
  saldo: number;
  historico: Historico[];
  /** PTAX dos itens em moeda estrangeira: valor usado e data de referência da cotação da versão aceita (só exibição). */
  ptaxReferencias?: Record<string, { valor: number; data: string | null }>;
}
export interface DocCompleto extends DocResumo {
  pedidoCompleto: PedidoDaOrdem;
  observacoes: string;
  emitida: Marco | null;
  cancelamento: (Marco & { motivo: string }) | null;
  historico: Historico[];
}
interface OpcoesOrdens {
  hoje: string;
  motivos_cancelamento: string[];
  entidades: { id: string; razao: string; fantasia: string; cnpj: string; grupoId: string; grupo: string }[];
}

export function SeloOrdem({ tipo }: { tipo: 'venda' | 'locacao' | 'trade_in' }) {
  const texto = tipo === 'venda' ? 'Ordem de Compra ao Fornecedor (OCF)' : tipo === 'locacao' ? 'Ordem de Locação ao Parceiro (OL, provisório)' : 'Ordem de Trade-in ao Comprador (OTI, provisório)';
  return (
    <span className={`selo selo-ordem selo-ordem-${tipo}`} data-teste="selo-ordem">
      {texto}
    </span>
  );
}

const produtoDe = (p: PedidoDaOrdem, itemId: string): ProdutoCopia | undefined => p.conteudo.itens.find((x) => x.itemId === itemId)?.produtoCopia;
const linhasOrdem = (o: Pick<OcfResumo, 'itens'>) => o.itens.map((i) => ({ quantidade: i.quantidade, custoUnit: i.custoUnit, vendaUnit: i.precoUnit }));

/* =============================== Lista (visão global) =============================== */

type DataOrdem = 'criacao' | 'faturamento';
// P3-37: "faturamento" = lançamento ou estorno de NF dentro do período (cada evento na sua data), não a data do último
const DATAS_ORDEM: { id: DataOrdem; rotulo: string; curto: string }[] = [
  { id: 'criacao', rotulo: 'Data de criação da Ordem', curto: 'criação' },
  { id: 'faturamento', rotulo: 'Lançamento de NF no período', curto: 'faturamento' },
];
type ColOcf = 'numero' | 'cliente' | 'entidade' | 'pedido' | 'criacao' | 'faturamento' | 'situacao' | 'progresso' | 'faturado' | 'saldo' | 'custo' | 'venda' | 'margem';
/** P3-36: percentual agregado (margem agregada / valor agregado); traço sem base. */
const pctOuTraco = (x: Valores) => {
  const p = percentualDe(x);
  return p === null ? '—' : pct(p);
};
type ColDoc = 'numero' | 'tipo' | 'pedido' | 'cliente' | 'parceiro' | 'situacao' | 'criacao' | 'valor' | 'comissao';
type SituacaoFiltro = EstadoOrdem | 'em_conferencia' | 'emitida';
type TipoOrdemFiltro = '' | 'venda' | 'locacao' | 'trade_in';
/** P2-15 (D9): Status conforme o Tipo de Ordem. OCF não tem "Emitida" (emissão é um marco; ver critério "Emitida ao fornecedor"). */
/** P4 bloco 3 (I09): ordem do fluxo — em aberto, faturados, parciais e cancelados por último (estados internos inalterados). */
const STATUS_OCF: { id: SituacaoFiltro; nome: string }[] = [
  { id: 'backlog', nome: 'Backlog' },
  { id: 'faturada', nome: 'Faturada' },
  { id: 'faturada_parcialmente', nome: 'Faturada parcialmente' },
  { id: 'faturada_saldo_cancelado', nome: 'Faturada parcialmente · saldo cancelado' },
  { id: 'cancelada', nome: 'Cancelada' },
];
const STATUS_DOC: { id: SituacaoFiltro; nome: string }[] = [
  { id: 'em_conferencia', nome: 'Em conferência' },
  { id: 'emitida', nome: 'Emitida' },
  { id: 'cancelada', nome: 'Cancelada' },
];
const STATUS_TODAS: { id: SituacaoFiltro; nome: string }[] = [
  { id: 'backlog', nome: 'Backlog (OCF)' },
  { id: 'em_conferencia', nome: 'Em conferência (OL/OTI)' },
  { id: 'emitida', nome: 'Emitida (OL/OTI)' },
  { id: 'faturada', nome: 'Faturada (OCF)' },
  { id: 'faturada_parcialmente', nome: 'Faturada parcialmente (OCF)' },
  { id: 'faturada_saldo_cancelado', nome: 'Faturada parcialmente · saldo cancelado (OCF)' },
  { id: 'cancelada', nome: 'Cancelada (todas)' },
];
const COR_STATUS_ORDEM: Record<SituacaoFiltro, string> = { backlog: 'aberto', em_conferencia: 'aberto', emitida: 'emitida', faturada: 'faturado', faturada_parcialmente: 'parcial-fat', faturada_saldo_cancelado: 'saldo', cancelada: 'cancelado' };
const TIPOS_ORDEM_SELETOR: { id: TipoOrdemFiltro; nome: string }[] = [
  { id: '', nome: 'Todos' },
  { id: 'venda', nome: 'OCF' },
  { id: 'locacao', nome: 'OL' },
  { id: 'trade_in', nome: 'OTI' },
];
const statusDoTipo = (t: TipoOrdemFiltro) => (t === 'venda' ? STATUS_OCF : t === '' ? STATUS_TODAS : STATUS_DOC);
const todosDoTipo = (t: TipoOrdemFiltro) => statusDoTipo(t).map((s) => s.id);

export function ListaOrdens() {
  const carga = useCarga(() => api.get<{ ocf: OcfResumo[]; documentos: DocResumo[] }>('/api/ordens'), []);
  const [tipoOrdem, setTipoOrdem] = useState<TipoOrdemFiltro>('');
  const [situacoes, setSituacoes] = useState<SituacaoFiltro[]>(todosDoTipo(''));
  const [emitidaForn, setEmitidaForn] = useState('');
  const [grupo, setGrupo] = useState('');
  const [entidade, setEntidade] = useState('');
  const [cliente, setCliente] = useState('');
  const [busca, setBusca] = useState('');
  const per = usePeriodo<DataOrdem>('criacao');
  const ord = useOrdenacao<ColOcf>('criacao', 'desc');
  // P2-03/P2-04 (D7): visões da tela (filtros + ordenação + colunas da tabela OCF)
  const pers = usePersonalizacao('ordens-ocf', { busca: { valor: busca, definir: setBusca },
    filtros: { tipoOrdem, situacoes, emitidaForn, grupo, entidade, cliente, periodo: per.periodo, data: per.data },
    aplicarFiltros: (e: Record<string, unknown>) => {
      const t = (e.tipoOrdem as TipoOrdemFiltro) ?? '';
      setTipoOrdem(t);
      setSituacoes((e.situacoes as SituacaoFiltro[]) ?? todosDoTipo(t));
      setEmitidaForn((e.emitidaForn as string) ?? '');
      setGrupo((e.grupo as string) ?? '');
      setEntidade((e.entidade as string) ?? '');
      setCliente((e.cliente as string) ?? '');
      if (e.periodo) per.setPeriodo(e.periodo as typeof per.periodo);
      if (e.data) per.setData(e.data as DataOrdem);
    },
    ordenacao: ord,
  });
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="ordens" />;
  const hoje = hojeIso();
  const { ocf, documentos } = carga.dados;
  const limpar = () => {
    setTipoOrdem('');
    setSituacoes(todosDoTipo(''));
    setEmitidaForn('');
    setGrupo('');
    setEntidade('');
    setCliente('');
    setBusca('');
    per.setPeriodo(periodoInicial(hoje));
    per.setData('criacao');
  };
  // Visão global: OCF (Venda) e OL/OTI com os MESMOS filtros e a mesma busca.
  type Global = { id: string; numero: string; tipo: 'venda' | 'locacao' | 'trade_in'; clienteId: string; entidade: EntidadeOrdem | null; situacao: SituacaoFiltro; criacao: string; faturouNoPeriodo?: boolean; emitidaFornecedor?: boolean; chaves: Chave[] };
  // P3-37 (D4): faturamento do período por lançamento e estorno; acumulado e carteira no fim do período (até hoje)
  const iv = intervalo(per.periodo, hoje);
  const inicio = iv.de ?? '0000-01-01';
  const fim = iv.ate && iv.ate < hoje ? iv.ate : hoje;
  const dentro = (d: string | null) => d !== null && d.slice(0, 10) >= inicio && d.slice(0, 10) <= fim;
  const apuracao = new Map(ocf.map((o) => [o.id, o.conc ? apurarOrdem(o.conc, inicio, fim) : null]));
  const globais: Global[] = [
    ...ocf.map((o) => ({ id: o.id, numero: o.numero, tipo: 'venda' as const, clienteId: o.cliente.id, entidade: o.entidade, situacao: o.situacao as SituacaoFiltro, criacao: o.dataCriacao, faturouNoPeriodo: !iv.de || Boolean(o.conc?.lancamentos.some((f) => dentro(f.dataHora) || dentro(f.estornoEm))), emitidaFornecedor: o.emitidaFornecedor, chaves: o.chaves })),
    ...documentos.map((x) => ({ id: x.id, numero: x.numero, tipo: x.tipo, clienteId: x.cliente.id, entidade: x.entidade, situacao: x.situacao as SituacaoFiltro, criacao: x.dataCriacao, chaves: x.chaves })),
  ];
  const clientes = [...new Map([...ocf, ...documentos].map((x) => [x.cliente.id, x.cliente])).values()].sort((a, b) => a.nome.localeCompare(b.nome));
  const entidades = [...new Map(globais.flatMap((x) => (x.entidade ? [[x.entidade.id, x.entidade] as const] : []))).values()];
  const parceiros = [...new Map(entidades.map((e) => [e.grupoId, { id: e.grupoId, nome: e.grupo }])).values()].sort((a, b) => a.nome.localeCompare(b.nome));
  const achados = buscar(globais, (o) => o.chaves, busca);
  const motivo = new Map((achados ?? []).map((r) => [r.item.id, textoMotivo(r, 'ordem')]));
  const entidadesDoGrupo = entidades.filter((e) => !grupo || e.grupoId === grupo);
  const passa = (o: Global) =>
    (!achados || motivo.has(o.id)) &&
    (!cliente || o.clienteId === cliente) &&
    (!tipoOrdem || o.tipo === tipoOrdem) &&
    situacoes.includes(o.situacao) &&
    (!emitidaForn || (o.tipo === 'venda' && (emitidaForn === 'sim') === Boolean(o.emitidaFornecedor))) &&
    (!grupo || (grupo !== 'sem' && o.entidade?.grupoId === grupo)) &&
    (!entidade || o.entidade?.id === entidade) &&
    (per.data === 'criacao' ? noPeriodo(per.periodo, o.criacao, hoje) : Boolean(o.faturouNoPeriodo));
  const filtradas = globais.filter(passa);
  // P4 bloco 3 (I09): contagens do seletor ignoram Tipo e Status; os chips, só o Status (o seletor e o filtro Tipo são o mesmo estado)
  const passaSem = (o: Global) =>
    (!achados || motivo.has(o.id)) && (!cliente || o.clienteId === cliente) && (!emitidaForn || (o.tipo === 'venda' && (emitidaForn === 'sim') === Boolean(o.emitidaFornecedor))) && (!grupo || (grupo !== 'sem' && o.entidade?.grupoId === grupo)) && (!entidade || o.entidade?.id === entidade) && (per.data === 'criacao' ? noPeriodo(per.periodo, o.criacao, hoje) : Boolean(o.faturouNoPeriodo));
  const semTipo = globais.filter(passaSem);
  const contTipo = (t: TipoOrdemFiltro) => (t ? semTipo.filter((o) => o.tipo === t).length : semTipo.length);
  const doTipoAtual = semTipo.filter((o) => !tipoOrdem || o.tipo === tipoOrdem);
  const chipsStatus = statusDoTipo(tipoOrdem).map((st) => ({ nome: st.nome.replace(' (todas)', ''), n: doTipoAtual.filter((o) => o.situacao === st.id).length, cor: COR_STATUS_ORDEM[st.id] }));
  const escolherTipo = (t: TipoOrdemFiltro) => {
    setTipoOrdem(t);
    setSituacoes(todosDoTipo(t));
    if (t !== 'venda' && t !== '') setEmitidaForn('');
  };
  const ids = new Set(filtradas.map((o) => o.id));
  const lista = ord.ordenar(
    ocf.filter((o) => ids.has(o.id)),
    {
      numero: (o) => o.numero,
      cliente: (o) => o.cliente.nome,
      entidade: (o) => o.entidade.razao || o.entidade.fantasia,
      pedido: (o) => o.pedido.numero,
      criacao: (o) => o.dataCriacao,
      faturamento: (o) => o.dataFaturamento,
      situacao: (o) => ESTADOS_ORDEM[o.situacao],
      progresso: (o) => (o.progresso.total ? o.progresso.faturada / o.progresso.total : 0),
      faturado: (o) => apuracao.get(o.id)?.faturado.v ?? 0,
      saldo: (o) => apuracao.get(o.id)?.carteira.v ?? 0,
      custo: (o) => apuracao.get(o.id)?.mantido.c ?? 0,
      venda: (o) => apuracao.get(o.id)?.mantido.v ?? 0,
      margem: (o) => margemDe(apuracao.get(o.id)?.mantido ?? ZERO),
    },
    (a, b) => b.numero.localeCompare(a.numero),
  );
  const docs = documentos.filter((x) => ids.has(x.id));
  const comSaldoCancelado = lista.filter((o) => o.situacao === 'faturada_saldo_cancelado').length;
  // P3-35 (D4): base única — a parte executada fica (faturado), só o cancelado sai; a substituta não duplica o compromisso
  const total = (k: 'noPeriodo' | 'faturado' | 'carteira' | 'mantido' | 'cancelado') => somar(...lista.map((o) => apuracao.get(o.id)?.[k] ?? ZERO));
  const [noPeriodo_, faturado, carteira, mantido, cancelado] = [total('noPeriodo'), total('faturado'), total('carteira'), total('mantido'), total('cancelado')];
  const dm = dataBr(fim).slice(0, 5);
  const ols = docs.filter((x) => x.tipo === 'locacao');
  const otis = docs.filter((x) => x.tipo === 'trade_in');
  const verVenda = tipoOrdem === '' || tipoOrdem === 'venda';
  // P3-31/P3-34: apuração da Locação pelos registros do acompanhamento (sem registro = "A apurar", nunca zero)
  const somaPeriodo = (xs: { data: string; valor: number }[]) => xs.filter((x) => x.data >= inicio && x.data <= fim).reduce((s, x) => s + x.valor, 0);
  // Contas a Receber E1: mesma fonte do Financeiro, só para quem tem acesso ao Receber (nulo = restrito)
  const verFinanceiroOl = ols.some((x) => x.locacao?.pagamentos !== null && x.locacao?.pagamentos !== undefined);
  const recebidosOl = ols.flatMap((x) => x.locacao?.recebimentos ?? []);
  const pagosOl = ols.flatMap((x) => x.locacao?.pagamentos ?? []);
  const recebidoOl = recebidosOl.length ? somaPeriodo(recebidosOl) : null;
  const pagoOl = pagosOl.length ? somaPeriodo(pagosOl) : null;
  // P4 bloco 3 (I08): mesmas bases de Pedidos — OL: total do contrato e líquido do parceiro; OTI: avaliação bruta e valor ao Cliente
  const docsBase = documentos.filter((x) => semTipo.some((g) => g.id === x.id));
  const olAtivas = docsBase.filter((x) => x.tipo === 'locacao' && x.situacao !== 'cancelada');
  const olTot: Valores = { q: olAtivas.length, v: olAtivas.reduce((a2, x) => a2 + x.valores.valorCliente, 0), c: olAtivas.reduce((a2, x) => a2 + x.valores.valorCliente - x.valores.comissao, 0) };
  const otiValidas = docsBase.filter((x) => x.tipo === 'trade_in' && x.situacao !== 'cancelada');
  const otiTot: Valores = { q: otiValidas.length, v: otiValidas.reduce((a2, x) => a2 + x.valores.venda, 0), c: otiValidas.reduce((a2, x) => a2 + x.valores.valorCliente, 0) };
  return (
    <>
      {/* P4 bloco 3 (I08/I09, rev4 aprovada): seletor e Status no componente de Pedidos; bases separadas por modalidade */}
      <section className="resumo-destaque resumo-pedidos-tipos" data-teste="indicadores-ordens" aria-label="Resumo das Ordens">
        <div className="linha-seletor">
          <div className="seletor-tipo-pedido" role="group" aria-label="Modalidade" data-teste="seletor-tipo-ordem">
            {TIPOS_ORDEM_SELETOR.map((t) => (
              <button key={t.id || 'todos'} type="button" className={tipoOrdem === t.id ? 'ativo' : ''} aria-pressed={tipoOrdem === t.id} onClick={() => escolherTipo(t.id)} data-teste={`seletor-ordem-${t.id || 'todos'}`}>
                {t.nome} <b>{contTipo(t.id)}</b>
              </button>
            ))}
          </div>
          <span className="posicao-resumo" data-teste="posicao-ordens">
            Posição em {dataBr(fim)}
            <Ajuda titulo="Posição e período">{`Os valores são a posição acumulada em ${dataBr(fim)}: faturado pelos lançamentos de NF válidos até a data (estorno na data do estorno) e saldo executável das Ordens vigentes. O movimento do período filtrado aparece na Composição. Cada modalidade tem a sua base; nenhum total soma bases diferentes. Valores brutos, sem frete e sem abatimento.`}</Ajuda>
          </span>
        </div>
        {tipoOrdem === '' && (
          <div className="quadros-modalidade">
            <section className="quadro-modalidade" data-teste="quadro-ocf">
              <h3>OCF · Venda</h3>
              <dl>
                <dt>Faturado</dt>
                <dd data-teste="quadro-ocf-faturado">{moeda2(faturado.v)}</dd>
                <dt>Saldo a faturar</dt>
                <dd data-teste="quadro-ocf-saldo">{moeda2(carteira.v)}</dd>
                <dt>Margem do faturado</dt>
                <dd>
                  {moeda2(margemDe(faturado))} ({pctOuTraco(faturado)})
                </dd>
              </dl>
            </section>
            <section className="quadro-modalidade" data-teste="quadro-ol">
              <h3>OL · Locação</h3>
              <dl>
                <dt>OLs ativas</dt>
                <dd data-teste="quadro-ol-ativas">{olAtivas.length}</dd>
                <dt>Total previsto</dt>
                <dd data-teste="quadro-ol-total">{moeda2(olTot.v)}</dd>
                <dt>Margem bruta total</dt>
                <dd>
                  {moeda2(margemDe(olTot))} ({pctOuTraco(olTot)})
                </dd>
              </dl>
            </section>
            <section className="quadro-modalidade" data-teste="quadro-oti">
              <h3>OTI · Trade-in</h3>
              <dl>
                <dt>Avaliação bruta</dt>
                <dd data-teste="quadro-oti-avaliacao">{moeda2(otiTot.v)}</dd>
                <dt>Valor ao Cliente</dt>
                <dd data-teste="quadro-oti-cliente">{moeda2(otiTot.c)}</dd>
                <dt>Margem bruta</dt>
                <dd data-teste="quadro-oti-margem">
                  {moeda2(margemDe(otiTot))} ({pctOuTraco(otiTot)})
                </dd>
              </dl>
            </section>
          </div>
        )}
        {tipoOrdem === 'venda' && (
          <>
            <CardsLista n={3} teste="cards-venda">
              <CardLista icone="dinheiro" tom="azul" rotulo="Faturado" valor={moeda2(faturado.v)} detalhe={`${faturado.q} un.`} teste="card-faturado" />
              <CardLista icone="relogio" tom="verde" rotulo="Saldo a faturar" valor={moeda2(carteira.v)} detalhe={`${carteira.q} un.`} teste="card-saldo" />
              <CardLista icone="grafico" tom="roxo" rotulo="Margem do faturado" valor={moeda2(margemDe(faturado))} detalhe={pctOuTraco(faturado)} teste="card-margem-faturado" />
            </CardsLista>
            <details className="detalhes-financeiros composicao-ordens" data-teste="composicao-ocf">
              <summary>Composição e margens</summary>
              <dl className="pares-detalhes">
                {iv.de && (
                  <div data-teste="card-faturado-periodo">
                    <dt>Faturado no período</dt>
                    <dd>
                      {moeda2(noPeriodo_.v)} · margem {moeda2(margemDe(noPeriodo_))} ({pctOuTraco(noPeriodo_)})
                    </dd>
                  </div>
                )}
                <div data-teste="card-valor-mantido">
                  <dt>Valor mantido nas Ordens</dt>
                  <dd>{moeda2(mantido.v)}</dd>
                </div>
                <div data-teste="card-margem-total">
                  <dt>Margem total</dt>
                  <dd>
                    {moeda2(margemDe(mantido))} ({pctOuTraco(mantido)})
                  </dd>
                </div>
                <div data-teste="card-margem-realizar">
                  <dt>Margem a realizar</dt>
                  <dd>
                    {moeda2(margemDe(carteira))} ({pctOuTraco(carteira)})
                  </dd>
                </div>
                <div data-teste="card-saldo-cancelado-ocf">
                  <dt>(−) Saldo cancelado sem substituta</dt>
                  <dd>{moeda2(cancelado.v)}</dd>
                </div>
                <div data-teste="card-qtd-ocf">
                  <dt>Ordens de Venda</dt>
                  <dd>
                    {lista.length} · {lista.filter((o) => o.situacao === 'cancelada').length} cancelada(s){comSaldoCancelado ? ` · ${comSaldoCancelado} com saldo cancelado` : ''}
                  </dd>
                </div>
              </dl>
            </details>
          </>
        )}
        {tipoOrdem === 'locacao' && (
          <>
            <CardsLista n={3} teste="cards-locacao">
              <CardLista icone="ordens" tom="azul" rotulo="OLs ativas" valor={olAtivas.length} detalhe={`${ols.length - olAtivas.length} cancelada(s)`} teste="card-qtd-ol" />
              <CardLista icone="dinheiro" tom="verde" rotulo="Total previsto" valor={moeda2(olTot.v)} teste="card-total-previsto-ol" ajuda={<Ajuda titulo="Total previsto">Soma do total do contrato das OLs ativas, na base contratada, sem projetar reajustes.</Ajuda>} />
              <CardLista icone="grafico" tom="roxo" rotulo="Margem bruta total" valor={moeda2(margemDe(olTot))} detalhe={pctOuTraco(olTot)} teste="card-margem-ol" />
            </CardsLista>
            <details className="detalhes-financeiros composicao-ordens" data-teste="composicao-ol">
              <summary>Composição e margens</summary>
              <dl className="pares-detalhes">
                <div>
                  <dt>Mensalidades (OLs ativas, cobrança mensal)</dt>
                  <dd>{moeda2(olAtivas.filter((x) => x.locacao?.periodicidade === 'Mensal').reduce((a2, x) => a2 + (x.locacao?.mensalidade ?? 0), 0))}/mês</dd>
                </div>
                <div data-teste="card-recebido-ol">
                  <dt>{iv.de ? 'Recebido pela Inttrix no período' : 'Recebido pela Inttrix'}</dt>
                  <dd>{!verFinanceiroOl ? <span className="a-apurar">Restrito ao Financeiro</span> : recebidoOl === null ? <span className="a-apurar">Sem recebimento registrado no Contas a Receber</span> : moeda2(recebidoOl)}</dd>
                </div>
                <div data-teste="card-pago-ol">
                  <dt>{iv.de ? 'Pago pelo Cliente ao parceiro no período' : 'Pago pelo Cliente ao parceiro'}</dt>
                  <dd>
                    {!verFinanceiroOl ? <span className="a-apurar">Restrito ao Financeiro</span> : pagoOl === null ? <span className="a-apurar">A apurar</span> : moeda2(pagoOl)} <span className="texto-secundario">(Contas a Receber · não é receita da Inttrix)</span>
                  </dd>
                </div>
              </dl>
            </details>
          </>
        )}
        {tipoOrdem === 'trade_in' && (
          <CardsLista n={3} teste="cards-tradein">
            <CardLista icone="moedas" tom="azul" rotulo="Avaliação bruta" valor={moeda2(otiTot.v)} teste="card-avaliacao-oti" ajuda={<Ajuda titulo="Avaliação bruta">Soma das avaliações dos compradores nas OTIs válidas. Não é venda da Inttrix.</Ajuda>} />
            <CardLista icone="dinheiro" tom="verde" rotulo="Valor ao Cliente" valor={moeda2(otiTot.c)} teste="card-valor-cliente-oti" />
            <CardLista icone="grafico" tom="roxo" rotulo="Margem bruta" valor={moeda2(margemDe(otiTot))} detalhe={pctOuTraco(otiTot)} teste="card-margem-oti" />
          </CardsLista>
        )}
        <ChipsStatus itens={chipsStatus} teste="status-ordens" />
      </section>
      {/* P2-16: a busca é o primeiro campo da barra de filtros; a ajuda "?" fica à direita */}
      <BarraFiltros
        teste="ordens"
        direita={<>{pers.controles}<Ajuda titulo="Como os valores são somados">{`Cada modalidade tem o seu resumo (nunca somados). Venda: faturado pelos lançamentos de NF até o fim do período, saldo executável na mesma data; a parte faturada de uma Ordem cancelada continua contando. ${NOTA_FORNECEDOR}`}</Ajuda></>}
        fixos={
          <>
            <CampoBusca valor={busca} setValor={setBusca} rotulo="Buscar ordem" ajuda="OCF-, PED-, NF, cliente ou CNPJ" dica="OCF-, OL-, OTI- ou número, PED- de origem, Oportunidade, Proposta, OC do Cliente, NF, Cliente, parceiro ou CNPJ" teste="busca-ordens" total={achados ? filtradas.length : null} />
            <label>
              Tipo de Ordem
              <select value={tipoOrdem} onChange={(e) => escolherTipo(e.target.value as TipoOrdemFiltro)} data-teste="filtro-tipo-ordem">
                <option value="">Todas</option>
                <option value="venda" title="Ordem de Compra ao Fornecedor">OCF (Venda)</option>
                <option value="locacao" title="Ordem de Locação ao Parceiro">OL (Locação)</option>
                <option value="trade_in" title="Ordem de Trade-in ao Comprador">OTI (Trade-in)</option>
              </select>
            </label>
            {/* P2-15 (D9): a lista de Status acompanha o Tipo de Ordem; padrão A com Aplicar (D13) */}
            <FiltroMultiplo key={tipoOrdem || 'todas'} rotulo="Status" opcoes={statusDoTipo(tipoOrdem)} valor={situacoes} padrao={todosDoTipo(tipoOrdem)} aoAplicar={setSituacoes} teste="filtro-situacao" prefixo="status-ordem" rotuloGrupo="Status da Ordem" />
          </>
        }
        opcionais={[
          filtroSelecao('cliente', 'Cliente', cliente, setCliente, clientes, 'filtro-cliente-ordens', 'Todos os clientes'),
          filtroSelecao('parceiro', 'Parceiro comercial', grupo, (g) => (setGrupo(g), setEntidade('')), [...parceiros, { id: 'sem', nome: 'Sem parceiro definido' }], 'filtro-grupo', 'Todos os parceiros'),
          filtroSelecao('entidade', 'Entidade faturadora', entidade, setEntidade, entidadesDoGrupo.map((e) => ({ id: e.id, nome: `${e.fantasia} — ${e.cnpj}` })), 'filtro-entidade', `Todas as entidades${grupo ? ' do parceiro' : ''}`),
          ...(tipoOrdem === '' || tipoOrdem === 'venda' ? [filtroSelecao('emitida-fornecedor', 'Emitida ao fornecedor', emitidaForn, setEmitidaForn, [{ id: 'sim', nome: 'Sim (OCF emitida)' }, { id: 'nao', nome: 'Não (ainda não emitida)' }], 'filtro-emitida-fornecedor', 'Todas')] : []),
          filtroPeriodo(per, DATAS_ORDEM, 'criacao'),
        ]}
        aoLimpar={limpar}
      />
      {filtradas.length === 0 && <Estado tipo="vazio" titulo={situacoes.length === 0 ? 'nenhum status selecionado — use Todas ou escolha ao menos um' : 'nenhuma ordem com a busca e os filtros atuais'} teste="ordens-vazio-filtros" />}
      {(tipoOrdem === '' || tipoOrdem === 'venda') && lista.length > 0 && (
        <>
          <h2>Ordens de Compra ao Fornecedor (OCF)</h2>
          <div className="rolagem quadro-tabela">
            <table ref={pers.tabela} className="tabela tabela-ordens" data-teste="lista-ordens">
              <thead>
                <tr>
                  <Th ord={ord} coluna="numero" padrao="desc" className="col-fixa">
                    Número
                  </Th>
                  <Th ord={ord} coluna="cliente">Cliente / CNPJ atendido</Th>
                  <Th ord={ord} coluna="entidade">Entidade faturadora (fornecedor)</Th>
                  <Th ord={ord} coluna="pedido">Pedido</Th>
                  <Th ord={ord} coluna="situacao">Status</Th>
                  <Th ord={ord} coluna="progresso" padrao="desc">Progresso</Th>
                  <Th ord={ord} coluna="faturado" padrao="desc" numero>Faturado (R$)</Th>
                  <Th ord={ord} coluna="saldo" padrao="desc" numero>Saldo a faturar (R$)</Th>
                  <Th ord={ord} coluna="margem" padrao="desc" numero>
                    Margem<Ajuda titulo="Margem da lista">{`Margem prevista do valor mantido: parte faturada (na base de cada NF) + saldo a faturar em ${dm}. Não é margem recebida.`}</Ajuda>
                  </Th>
                  <Th ord={ord} coluna="custo" padrao="desc" numero>Custo</Th>
                  <Th ord={ord} coluna="venda" padrao="desc" numero>Valor mantido</Th>
                  <Th ord={ord} coluna="criacao" padrao="desc">Criação</Th>
                  <Th ord={ord} coluna="faturamento" padrao="desc">Último faturamento</Th>
                </tr>
              </thead>
              <tbody>
                {lista.map((o) => {
                  const a = apuracao.get(o.id) ?? { noPeriodo: ZERO, faturado: ZERO, carteira: ZERO, mantido: ZERO, cancelado: ZERO };
                  return (
                  <tr key={o.id}>
                    <td className="col-fixa nowrap">
                      <a href={`#/ordens/${o.numero}`}>{o.numero}</a>
                      {motivo.get(o.id) && motivo.get(o.id) !== `Encontrado pela Ordem ${o.numero}` && (
                        <span className="motivo-busca" data-teste={`motivo-${o.numero}`}>
                          {motivo.get(o.id)}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="duas-linhas" title={o.cliente.razao || o.cliente.nome}>
                        {o.cliente.razao || o.cliente.nome}
                      </span>
                      {o.cliente.cnpj && <div className="texto-secundario nowrap">{o.cliente.cnpj}</div>}
                    </td>
                    <td>
                      <span className="duas-linhas" title={`${o.entidade.razao || o.entidade.fantasia} (${o.entidade.grupo})`}>
                        {o.entidade.razao || o.entidade.fantasia}
                      </span>
                      <div className="texto-secundario nowrap">{o.entidade.cnpj}</div>
                    </td>
                    <td className="nowrap">
                      <a href={`#/pedidos/${o.pedido.numero}`}>{o.pedido.numero}</a>
                    </td>
                    <td>
                      <SeloSituacao texto={ESTADOS_ORDEM[o.situacao]} tom={tomOrdem(o.situacao)} />
                      {o.substituta && <span className="rotulo-substituida">Substituída pela {o.substituta}</span>}
                    </td>
                    <td className="nowrap">
                      {o.progresso.faturada} de {o.progresso.total} faturadas
                    </td>
                    <td className="nowrap numero" data-teste={`faturado-${o.numero}`}>{moeda(a.faturado.v)}</td>
                    <td className="nowrap numero" data-teste={`saldo-${o.numero}`}>{moeda(a.carteira.v)}</td>
                    <td className={`nowrap numero${margemDe(a.mantido) < 0 ? ' negativo' : ''}`} data-teste={`margem-${o.numero}`}>
                      {moeda(margemDe(a.mantido))} <span className="texto-secundario">{pctOuTraco(a.mantido)}</span>
                    </td>
                    <td className="nowrap numero">{moeda(a.mantido.c)}</td>
                    <td className="nowrap numero">{moeda(a.mantido.v)}</td>
                    <td className="nowrap">{dataBr(o.dataCriacao)}</td>
                    <td className="nowrap">{o.dataFaturamento ? dataBr(o.dataFaturamento) : <span className="ausente">—</span>}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="rodape-tabela" data-teste="rodape-ocf">
              {lista.length} ordem(ns) de compra · faturado até {dm}: {moeda(faturado.v)} · saldo em {dm}: {moeda(carteira.v)} · margem {moeda(margemDe(mantido))}
            </div>
          </div>
        </>
      )}
      {/* P3-39: OL em lista própria (etapa e próxima ação do acompanhamento); OTI continua na sua lista */}
      {(tipoOrdem === '' || tipoOrdem === 'locacao') && ols.length > 0 && <ListaOl docs={ols} motivo={motivo} />}
      {(tipoOrdem === '' || tipoOrdem === 'trade_in') && otis.length > 0 && <ListaDocumentosOperacionais docs={otis} motivo={motivo} />}
    </>
  );
}

type ColOl = 'numero' | 'cliente' | 'locadora' | 'pedido' | 'etapa' | 'mensalidade' | 'prazo' | 'margem' | 'proxima' | 'valor' | 'criacao';
/** P3-39: lista própria de OL — Cliente/unidade e CNPJ, Locadora/CNPJ, Pedido, etapa, mensalidade, prazo, Margem e próxima ação. */
function ListaOl({ docs: base, motivo }: { docs: DocResumo[]; motivo: Map<string, string> }) {
  const ord = useOrdenacao<ColOl>('numero', 'desc');
  const pers = usePersonalizacao('ordens-ol', { semVisoes: true });
  const hoje = hojeIso();
  const docs = ord.ordenar(
    base,
    {
      numero: (d) => d.numero,
      cliente: (d) => d.cliente.razao || d.cliente.nome,
      locadora: (d) => d.entidade?.razao,
      pedido: (d) => d.pedido.numero,
      etapa: (d) => d.locacao?.etapa,
      mensalidade: (d) => d.locacao?.mensalidade ?? 0,
      prazo: (d) => d.locacao?.prazoMeses ?? 0,
      margem: (d) => d.locacao?.margemParcela ?? 0,
      proxima: (d) => d.locacao?.proximaAcao?.data ?? '9999',
      valor: (d) => d.valores.valorCliente,
      criacao: (d) => d.dataCriacao,
    },
    (a, b) => b.numero.localeCompare(a.numero),
  );
  return (
    <section data-teste="lista-ol">
      <div className="titulo-pag-linha">
        <h2 className="sem-margem">Ordens de Locação (OL)</h2>
        <span className="espaco" />
        {pers.controles}
      </div>
      <RolagemIndicada className="rolagem quadro-tabela" teste="lista-ol">
        <table ref={pers.tabela} className="tabela tabela-ol">
          <thead>
            <tr>
              <Th ord={ord} coluna="numero" padrao="desc" className="col-fixa">OL</Th>
              <Th ord={ord} coluna="cliente">Cliente / unidade</Th>
              <Th ord={ord} coluna="locadora">Locadora</Th>
              <Th ord={ord} coluna="pedido">Pedido</Th>
              <Th ord={ord} coluna="etapa">Etapa</Th>
              <Th ord={ord} coluna="mensalidade" padrao="desc" numero>Mensalidade</Th>
              <Th ord={ord} coluna="prazo" padrao="desc" numero>Prazo</Th>
              <Th ord={ord} coluna="margem" padrao="desc" numero>
                Margem<Ajuda titulo="Margem da OL">Margem prevista por parcela (base elegível × percentual da proposta aceita). Não é margem recebida: o recebimento da Inttrix é registrado no acompanhamento.</Ajuda>
              </Th>
              <Th ord={ord} coluna="proxima">Próxima ação</Th>
              <Th ord={ord} coluna="valor" padrao="desc" numero>Valor total do contrato</Th>
              <Th ord={ord} coluna="criacao" padrao="desc">Criação</Th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} data-teste={`doc-${d.numero}`}>
                <td className="col-fixa nowrap">
                  <a href={`#/ordens/${d.numero}`} title="Ordem de Locação ao Parceiro">{d.numero}</a>
                  {d.situacao === 'cancelada' && <span className="rotulo-substituida">Cancelada</span>}
                  {motivo.get(d.id) && motivo.get(d.id) !== `Encontrado pela Ordem ${d.numero}` && (
                    <span className="motivo-busca" data-teste={`motivo-${d.numero}`}>
                      {motivo.get(d.id)}
                    </span>
                  )}
                </td>
                <td>
                  <span className="duas-linhas" title={d.cliente.razao || d.cliente.nome}>
                    {d.cliente.razao || d.cliente.nome}
                  </span>
                  {d.cliente.cnpj && <div className="texto-secundario nowrap">{d.cliente.cnpj}</div>}
                </td>
                <td>
                  <span className="duas-linhas" title={d.entidade ? `${d.entidade.razao} (${d.entidade.grupo})` : ''}>
                    {d.entidade?.razao ?? '—'}
                  </span>
                  {d.entidade && <div className="texto-secundario nowrap">{d.entidade.cnpj}</div>}
                </td>
                <td className="nowrap">
                  <a href={`#/pedidos/${d.pedido.numero}`}>{d.pedido.numero}</a>
                </td>
                <td className="nowrap" data-teste={`etapa-${d.numero}`}>
                  <span className={`selo-previa ${d.locacao?.tomEtapa === 'ok' ? 'ok' : d.locacao?.tomEtapa === 'ni' ? 'ni' : 'pend'}`}>{d.locacao?.etapa ?? 'Não informado'}</span>
                  {d.locacao?.pendencia && <div className="texto-secundario duas-linhas" title={d.locacao.pendencia}>Pendência: {d.locacao.pendencia}</div>}
                </td>
                <td className="nowrap numero">{d.locacao ? moeda2(d.locacao.mensalidade) : '—'}</td>
                <td className="nowrap numero">{d.locacao ? `${d.locacao.prazoMeses} meses` : '—'}</td>
                <td className="nowrap numero">{d.locacao ? `${moeda2(d.locacao.margemParcela)}/parcela` : '—'}</td>
                <td>
                  {d.locacao?.proximaAcao ? (
                    <>
                      <span className="duas-linhas" title={d.locacao.proximaAcao.descricao}>{d.locacao.proximaAcao.descricao}</span>
                      <div className={`texto-secundario nowrap${d.locacao.proximaAcao.data < hoje ? ' negativo' : ''}`}>{dataBr(d.locacao.proximaAcao.data)}</div>
                    </>
                  ) : (
                    <span className="ausente">—</span>
                  )}
                </td>
                <td className="nowrap numero">{moeda2(d.valores.valorCliente)}</td>
                <td className="nowrap">{dataBr(d.dataCriacao)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </RolagemIndicada>
    </section>
  );
}

function ListaDocumentosOperacionais({ docs: base, motivo }: { docs: DocResumo[]; motivo: Map<string, string> }) {
  const ord = useOrdenacao<ColDoc>('criacao', 'desc');
  // D7: colunas da tabela OL/OTI (as visões da tela ficam na barra de filtros de Ordens)
  const pers = usePersonalizacao('ordens-documentos', { semVisoes: true });
  const docs = ord.ordenar(
    base,
    {
      numero: (d) => d.numero,
      tipo: (d) => d.tipo,
      pedido: (d) => d.pedido.numero,
      cliente: (d) => d.cliente.nome,
      parceiro: (d) => d.entidade?.grupo,
      situacao: (d) => d.situacaoTexto,
      criacao: (d) => d.dataCriacao,
      valor: (d) => d.valores.valorCliente,
      comissao: (d) => d.valores.comissao,
    },
    (a, b) => b.numero.localeCompare(a.numero),
  );
  return (
    <section data-teste="lista-documentos-operacionais">
      <div className="titulo-pag-linha">
        <h2 className="sem-margem">Ordens de Trade-in ao Comprador (OTI)</h2>
        <span className="espaco" />
        {pers.controles}
      </div>
      <div className="rolagem">
        <table ref={pers.tabela} className="tabela">
          <thead>
            <tr>
              <Th ord={ord} coluna="numero">Número</Th>
              <Th ord={ord} coluna="tipo">Tipo de ordem</Th>
              <Th ord={ord} coluna="pedido">Pedido</Th>
              <Th ord={ord} coluna="cliente">Cliente</Th>
              <Th ord={ord} coluna="parceiro">Parceiro comercial · entidade/CNPJ</Th>
              <Th ord={ord} coluna="situacao">Status</Th>
              <Th ord={ord} coluna="criacao" padrao="desc">Criação</Th>
              <Th ord={ord} coluna="valor" padrao="desc" numero>Valor ao Cliente</Th>
              <Th ord={ord} coluna="comissao" padrao="desc" numero>Margem</Th>
            </tr>
          </thead>
          <tbody>
            {docs.map((doc) => (
              <tr key={doc.id} data-teste={`doc-${doc.numero}`}>
                <td>
                  <a href={`#/ordens/${doc.numero}`}>{doc.numero}</a>
                  {motivo.get(doc.id) && motivo.get(doc.id) !== `Encontrado pela Ordem ${doc.numero}` && (
                    <span className="motivo-busca" data-teste={`motivo-${doc.numero}`}>
                      {motivo.get(doc.id)}
                    </span>
                  )}
                </td>
                <td>
                  <SeloOrdem tipo={doc.tipo} />
                </td>
                <td>
                  <a href={`#/pedidos/${doc.pedido.numero}`}>{doc.pedido.numero}</a> <SeloTipo tipo={doc.pedido.tipo} />
                </td>
                <td>{doc.cliente.nome}</td>
                <td>
                  {doc.entidade?.grupo ?? '—'}
                  <div className="texto-secundario">{doc.entidade ? `${doc.entidade.razao} — ${doc.entidade.cnpj}` : ''}</div>
                </td>
                <td>{doc.situacaoTexto}</td>
                <td>{dataBr(doc.dataCriacao)}</td>
                <td className="nowrap numero">
                  {moeda2(doc.valores.valorCliente)} {doc.tipo === 'locacao' ? '(contrato)' : '(a receber)'}
                </td>
                <td className="nowrap numero">{moeda2(doc.valores.comissao)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* =============================== Tela de uma Ordem =============================== */

/** /ordens/:numero — OCF ou OL/OTI pelo prefixo; `pdf` abre a folha do documento. */
export function TelaOrdem({ numero, pdf }: { numero: string; pdf?: boolean }) {
  const carga = useCarga(() => api.get<OcfCompleta | DocCompleto>(`/api/ordens/${numero}`), [numero]);
  useRotuloTrilha(carga.dados ? carga.dados.numero : null);
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="ordem" lixeira={carga.lixeira} />;
  const x = carga.dados;
  const aplicar = (novo: OcfCompleta | DocCompleto) => carga.setDados(novo);
  if (x.tipo === 'venda') return pdf ? <OrdemPdf o={x as OcfCompleta} /> : <Ocf o={x as OcfCompleta} aplicar={aplicar} recarregar={() => void carga.recarregar()} />;
  // P4 bloco 3 (I13/I16): OL com ficha e PDF próprios; a OTI mantém a ficha e o PDF atuais (fora do bloco)
  if (x.tipo === 'locacao') return pdf ? <OlPdf doc={x as DocCompleto} /> : <FichaOl doc={x as DocCompleto} aplicar={aplicar} />;
  return pdf ? <DocumentoOperacionalPdf doc={x as DocCompleto} /> : <DocumentoOperacional doc={x as DocCompleto} aplicar={aplicar} />;
}

function Historico({ itens }: { itens: Historico[] }) {
  return (
    <>
      <h2>Histórico</h2>
      <ul className="linha-tempo" data-teste="historico-ordem">
        {itens.map((h, i) => (
          <li key={i}>
            <strong>{quando(h.dataHora)}</strong> · {h.texto} <span className="texto-secundario">({h.autor})</span>
          </li>
        ))}
        {itens.length === 0 && <li className="texto-secundario">Sem registros.</li>}
      </ul>
    </>
  );
}

/** P4 bloco 3 (D1): Status da ficha — no Backlog, mostra a fase da emissão (a listagem mantém o estado calculado). */
function seloFichaOcf(o: OcfCompleta): { texto: string; tom: Tom } {
  if (o.situacao !== 'backlog') return { texto: ESTADOS_ORDEM[o.situacao], tom: tomOrdem(o.situacao) };
  return o.emitida || o.enviada ? { texto: 'Emitida', tom: 'aberto' } : { texto: 'Em conferência', tom: 'backlog' };
}
const MEIO_ENVIO: Record<'email' | 'portal' | 'outro', string> = { email: 'e-mail', portal: 'portal do fornecedor', outro: 'outro meio' };
const PAGADOR_CURTO: Record<'incluso' | 'inttrix' | 'cliente', string> = { incluso: 'incluso', inttrix: 'pago pela Inttrix', cliente: 'pago pelo Cliente' };

/**
 * Ficha da OCF — P4 bloco 3 (rev4 aprovada, refs. 04/06/07): cabeçalho compacto, resumo, acompanhamento com
 * Emissão → Envio ao fornecedor → Confirmação do fornecedor → Faturamento (D1), Parceiro e Inttrix na horizontal,
 * condições comerciais, Faturar/Entregar simétricos com IE e e-mail do documento (D2/D3), itens, detalhes e faturamentos.
 */
function Ocf({ o, aplicar, recarregar }: { o: OcfCompleta; aplicar: (x: OcfCompleta) => void; recarregar: () => void }) {
  const { pode } = useSessao();
  const [modal, setModal] = useState<null | 'conferir' | 'entrega' | 'envio' | 'confirmacao' | 'pedido' | 'faturar' | 'cancelar' | 'substituir'>(null);
  const [estornar, setEstornar] = useState<string | null>(null);
  const e = o.entidade;
  const pedido = o.pedidoCompleto;
  const c = pedido.conteudo;
  const fat = c.faturamento;
  const ent = c.entrega;
  const cad = pedido.cadastro;
  const eco = economia(linhasOrdem(o), o.frete);
  const ativa = !o.cancelamento;
  const podeEditar = pode('editar', 'ordem');
  const emitida = Boolean(o.emitida || o.enviada);
  const fechar = () => setModal(null);
  const salvar = (x: OcfCompleta) => (setModal(null), aplicar(x));
  const selo = seloFichaOcf(o);
  const mesmoEnd = ent.origem === 'mesmo endereço do faturamento' || enderecoTexto(ent.endereco) === enderecoTexto(fat.endereco);
  const pctMargem = o.valores.venda ? pct(o.valores.margem / o.valores.venda) : '—';
  const anterior = o.enviada?.registroAnterior;
  const progresso = (
    <span data-teste="progresso">
      {o.progresso.faturada} de {o.progresso.total} faturadas{o.cancelamento ? ` · saldo cancelado (${o.cancelamento.saldoCancelado})` : o.situacao === 'faturada' ? ' · concluído' : ''}
    </span>
  );
  const botaoConfirmacao = ativa && o.enviada && !o.confirmada && podeEditar && (
    <button type="button" className="botao-secundario pequeno" onClick={() => setModal('confirmacao')} data-teste="confirmar-ordem">
      Registrar confirmação
    </button>
  );
  const botaoFaturar = ativa && o.enviada && o.saldo > 0 && pode('faturar', 'ordem') && (
    <button type="button" className="botao-secundario pequeno" onClick={() => setModal('faturar')} data-teste="registrar-faturamento">
      Registrar faturamento
    </button>
  );
  const pedidoForn = o.confirmada && (
    <span className="pedido-fornecedor" data-teste="pedido-fornecedor">
      {o.confirmada.pedidoFornecedor ? (
        <>
          Pedido do fornecedor: <strong>{o.confirmada.pedidoFornecedor}</strong>
        </>
      ) : (
        <span className="ausente">Pedido do fornecedor não informado</span>
      )}
      {podeEditar && (
        <button type="button" className="botao-link" onClick={() => setModal('pedido')} title="Informar ou atualizar (mesma permissão da confirmação; fica no histórico)" aria-label="Informar ou atualizar o Pedido do fornecedor" data-teste="editar-pedido-fornecedor">
          ✎
        </button>
      )}
      {o.confirmada.alteracoesPedidoFornecedor.length > 1 && <span className="sec"> · {o.confirmada.alteracoesPedidoFornecedor.length - 1} alteração(ões) no histórico</span>}
    </span>
  );
  const etapas: Etapa[] = anterior
    ? [{ nome: 'Emissão e envio', sub: <>Registrados juntos em {quando(o.enviada!.dataHora)} · {o.enviada!.autor} <span className="selo selo-neutro">registro anterior</span></>, estado: 'concluida', teste: 'etapa-emissao' }]
    : [
        { nome: 'Emissão', sub: o.emitida ? `Emitida em ${quando(o.emitida.dataHora)} · ${o.emitida.autor}` : ativa ? 'Pendente' : '—', estado: o.emitida ? 'concluida' : ativa ? 'atual' : 'pendente', teste: 'etapa-emissao' },
        {
          nome: 'Envio ao fornecedor',
          sub: o.enviada?.envio ? `Enviada em ${quando(o.enviada.envio.dataHora)} · ${MEIO_ENVIO[o.enviada.envio.meio]}` : o.emitida && ativa ? 'Pendente — registre quando enviar' : 'Após a emissão',
          estado: o.enviada ? 'concluida' : o.emitida && ativa ? 'atual' : 'pendente',
          extra: o.enviada?.envio && (o.enviada.envio.destinatario || o.enviada.envio.evidencia) ? <span className="sec">{[o.enviada.envio.destinatario && `para ${o.enviada.envio.destinatario}`, o.enviada.envio.evidencia && `evidência: ${o.enviada.envio.evidencia.nome}`].filter(Boolean).join(' · ')}</span> : undefined,
          teste: 'etapa-envio',
        },
      ];
  etapas.push(
    {
      nome: 'Confirmação do fornecedor',
      sub: o.confirmada ? `Confirmada em ${o.confirmada.data ? dataBr(o.confirmada.data) : quando(o.confirmada.dataHora)}` : !ativa ? 'Não registrada' : o.enviada ? 'Pendente' : 'Aguardando envio',
      estado: o.confirmada ? 'concluida' : o.enviada && ativa ? 'atual' : 'pendente',
      extra: o.confirmada ? pedidoForn : botaoConfirmacao || undefined,
      teste: 'etapa-confirmacao',
    },
    {
      nome: 'Faturamento',
      sub: progresso,
      estado: o.situacao === 'faturada' || o.situacao === 'faturada_saldo_cancelado' ? 'concluida' : o.enviada && ativa ? 'atual' : 'pendente',
      extra: botaoFaturar || undefined,
      teste: 'etapa-faturamento',
    },
  );
  const acaoPrincipal =
    ativa && podeEditar && !emitida ? (
      <button type="button" className="botao-primario" onClick={() => setModal('conferir')} data-teste="conferir-emissao">
        Conferir emissão
      </button>
    ) : ativa && podeEditar && o.emitida && !o.enviada ? (
      <button type="button" className="botao-primario" onClick={() => setModal('envio')} data-teste="registrar-envio">
        Registrar envio
      </button>
    ) : undefined;
  return (
    <>
      <CabecalhoOrdem
        numero={
          <>
            {o.numero}
            {(o.revisao ?? 0) > 0 && <span className="revisao-titulo" data-teste="revisao-ordem"> revisão r{o.revisao}</span>}
          </>
        }
        tipo="Ordem de Compra ao Fornecedor"
        status={
          <span title={o.situacao === 'backlog' ? 'Na listagem: Backlog' : undefined} data-teste="status-ordem">
            <SeloSituacao texto={selo.texto} tom={selo.tom} />
          </span>
        }
        acoes={
          <>
            {o.substituta && (
              <a className="rotulo-substituida" href={`#/ordens/${o.substituta}`} data-teste="rotulo-substituida">
                Substituída pela {o.substituta}
              </a>
            )}
            <a className="botao-primario" href={`#/ordens/${o.numero}/pdf`} data-teste="pdf-ordem-link">
              <Icone nome="documento" /> Visualizar PDF
            </a>
            <BotaoExcluir entidade="ordem" id={o.id} aoExcluir={depoisDeExcluir(`/pedidos/${pedido.numero}`)} />
          </>
        }
        contexto={[
          <>
            Pedido: <a href={`#/pedidos/${pedido.numero}`}>{pedido.numero}</a>
          </>,
          <>
            Cliente final:{' '}
            <a href={`#/clientes/${pedido.cliente.id}`}>
              {pedido.cliente.nome} ({fat.cnpj})
            </a>
          </>,
          <a href={`#/oportunidades/${pedido.oportunidade.id}/propostas/${pedido.proposta.id}/v/${pedido.versao}`}>
            Proposta {pedido.proposta.numero} v{pedido.versao}
          </a>,
          o.origem ? (
            <>
              Substituta da <a href={`#/ordens/${o.origem}`}>{o.origem}</a>
            </>
          ) : null,
        ]}
      />

      {o.cancelamento && (
        <div className="aviso aviso-cancelada" data-teste="cancelamento">
          <strong>{o.situacao === 'faturada_saldo_cancelado' ? 'Saldo cancelado — estado definitivo (o faturamento realizado permanece).' : 'Cancelada — estado definitivo.'}</strong> Motivo: {o.cancelamento.motivo} · {o.situacao === 'faturada_saldo_cancelado' ? 'Saldo cancelado' : 'Cancelada'} por {o.cancelamento.autor} em {quando(o.cancelamento.dataHora).replace(' ', ' às ')}
          {o.cancelamento.observacao && ` · Observação: ${o.cancelamento.observacao}`}
          <br />⚠ Esta Ordem permanece {o.situacao === 'faturada_saldo_cancelado' ? 'sem saldo a faturar' : 'Cancelada'} para sempre. {o.substituta ? 'A substituta é um novo registro.' : ''}
        </div>
      )}

      <CardsLista n={4} teste="resumo-ordem">
        <CardLista icone="pedidos" tom="azul" rotulo="Valor da Ordem" valor={moeda2(o.valores.venda)} teste="card-valor-ordem" ajuda={<Ajuda titulo="Valor da Ordem">Venda ao Cliente dos itens desta Ordem (quantidades da Ordem × venda unitária da versão aceita). Não inclui frete.</Ajuda>} />
        <CardLista icone="fatura" tom="roxo" rotulo="Faturado" valor={moeda2(o.valores.faturado)} teste="card-faturado-ordem" />
        <CardLista icone="pizza" tom="verde" rotulo="Saldo a faturar" valor={moeda2(o.valores.saldo)} teste="card-saldo-ordem" />
        <CardLista icone="barras" tom="ambar" rotulo="Margem bruta" valor={moeda2(o.valores.margem)} detalhe={pctMargem} teste="card-margem-ordem" />
      </CardsLista>

      <Passos titulo="Acompanhamento da OCF" etapas={etapas} acao={acaoPrincipal} teste="marcos" />

      <Par>
        <QuadroCampos
          titulo="Parceiro"
          icone="predio"
          teste="fornecedor"
          acao={<Ajuda titulo="Parceiro">{NOTA_FORNECEDOR}</Ajuda>}
          campos={[
            [
              o.rotuloEntidade ?? 'Entidade faturadora',
              <>
                <span className="linha-empilhada" data-teste="fornecedor-razao">
                  {e.razao}
                </span>
                {/* o Parceiro comercial (grupo) só aparece quando o nome difere da entidade, para não repetir a mesma linha */}
                {e.grupo.trim().toLowerCase() !== e.razao.trim().toLowerCase() && (
                  <a className="linha-empilhada sec link-discreto" href={`#/administracao/parceiros/${e.grupoId}`}>
                    {e.grupo}
                  </a>
                )}
              </>,
            ],
            ['CNPJ', e.cnpj],
            ['Contato', (e.contato || e.email || e.telefone) && <Empilha itens={[e.contato, e.email && <a href={`mailto:${e.email}`}>{e.email}</a>, e.telefone].filter(Boolean) as ReactNode[]} />],
            ['Código de revenda', <span data-teste="codigo-revenda">{e.codigoRevenda}</span>],
          ]}
        />
        <QuadroCampos
          titulo="Inttrix"
          icone="predio"
          tom="neutro"
          campos={[
            ['Razão social', c.empresa?.razao ?? ''],
            ['CNPJ', c.empresa?.cnpj ?? ''],
            ['Responsável', <Empilha itens={[pedido.responsavel.nome, pedido.responsavel.email && <a href={`mailto:${pedido.responsavel.email}`}>{pedido.responsavel.email}</a>].filter(Boolean) as ReactNode[]} />],
          ]}
        />
      </Par>
      {e.papeis.includes('Parceiro de serviço') && <p className="aviso aviso-info">Serviço fornecido e executado pelo parceiro, não pela Inttrix.</p>}

      <FaixaCondicoes titulo="Condições comerciais" teste="condicoes-ordem" itens={[['Pagamento', pagamentoTexto(c.condicoes)], ['Frete', `${o.frete.modalidade} · ${PAGADOR_CURTO[o.frete.pagador]}${o.frete.valor ? ` · ${moeda2(o.frete.valor)}` : ''}`]]} />

      <Par>
        <QuadroLocal
          titulo="Faturar ao cliente final"
          icone="predio"
          tom="turquesa"
          teste="ordem-faturamento"
          razao={fat.razao}
          linhas={[
            <Junta partes={[`CNPJ ${fat.cnpj}`, <TextoHistorico rotulo="IE" v={cad.faturamento.ie} dataReferencia={cad.dataReferencia} teste="ie-faturamento" />]} />,
            enderecoTexto(fat.endereco),
            <Junta partes={[fat.contato && `Contato: ${fat.contato}`, <TextoHistorico rotulo="E-mail" v={cad.faturamento.email} dataReferencia={cad.dataReferencia} teste="email-faturamento" />, fat.telefone]} />,
            <>
              OC do Cliente: {pedido.ocsVigentes.length ? pedido.ocsVigentes.map((x, i) => <span key={x.numero}>{i > 0 && ' · '}<a href={`#/pedidos/${pedido.numero}`}>{x.numero}</a></span>) : <a href={`#/pedidos/${pedido.numero}`}>Não informada</a>}
            </>,
          ]}
        />
        <QuadroLocal
          titulo="Entregar em"
          icone="caminhao"
          tom="azul"
          teste="ordem-entrega"
          marca={mesmoEnd ? 'Mesmo endereço do faturamento' : undefined}
          acao={
            ativa && !emitida && podeEditar ? (
              <button type="button" className="botao-link editar-quadro" onClick={() => setModal('entrega')} data-teste="editar-entrega">
                ✎ Editar dados de entrega
              </button>
            ) : undefined
          }
          razao={ent.razao}
          linhas={[
            <Junta partes={[`CNPJ ${ent.cnpj}`, <TextoHistorico rotulo="IE" v={cad.entrega.ie} dataReferencia={cad.dataReferencia} teste="ie-entrega" />]} />,
            enderecoTexto(ent.endereco),
            <Junta partes={[`Contato: ${o.contatoEntrega || '— pendente'}`, o.telefoneEntrega || '— pendente']} />,
            o.observacoes ? `Observações: ${o.observacoes}` : null,
          ]}
        />
      </Par>

      <h2 className="titulo-tab">Itens da Ordem de Compra</h2>
      <div className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta" data-teste="itens-ordem">
          <thead>
            <tr>
              <th>PN</th>
              <th>Descrição</th>
              <th className="numero">Qtd</th>
              <th>Unid.</th>
              <th className="numero">Faturada</th>
              <th className="numero">Saldo</th>
              <th>Disponib.</th>
              <th>Moeda</th>
              <th className="numero">Custo unit.</th>
              <th className="numero">Custo total</th>
              <th className="numero">Venda unit.</th>
              <th className="numero">Venda total</th>
              <th className="numero">Margem R$</th>
              <th className="numero">Margem %</th>
            </tr>
          </thead>
          <tbody>
            {o.itens.map((i) => {
              const m = (i.precoUnit - i.custoUnit) * i.quantidade;
              const pr = produtoDe(pedido, i.itemId);
              return (
                <tr key={i.itemId}>
                  <td className="nowrap">{pr?.sku || '—'}</td>
                  <td className="celula-descricao-ordem" title={pr?.nome}>
                    {pr?.nome ?? i.itemId}
                  </td>
                  <td className="numero">{i.quantidade}</td>
                  <td>{pr?.unidade || 'UN'}</td>
                  <td className="numero">{i.faturada}</td>
                  <td className="numero">{o.cancelamento ? 0 : i.quantidade - i.faturada}</td>
                  <td>{i.disponibilidade || '—'}</td>
                  <td>{pr?.moeda ?? 'BRL'}</td>
                  <td className="numero nowrap">{moeda2(i.custoUnit)}</td>
                  <td className="numero nowrap">{moeda2(i.custoUnit * i.quantidade)}</td>
                  <td className="numero nowrap">{moeda2(i.precoUnit)}</td>
                  <td className="numero nowrap">{moeda2(i.precoUnit * i.quantidade)}</td>
                  <td className="numero nowrap">{moeda2(m)}</td>
                  <td className="numero">{pct(i.precoUnit * i.quantidade ? m / (i.precoUnit * i.quantidade) : 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <LinhaSecao titulo="Detalhes financeiros" icone="dinheiro" teste="economia-ordem">
        <dl className="pares-inline">
          <div>
            <dt>Custo total dos itens</dt>
            <dd>{moeda2(eco.custo)}</dd>
          </div>
          <div>
            <dt>Frete ({o.frete.modalidade} · {PAGADOR_CURTO[o.frete.pagador]})</dt>
            <dd data-teste="frete-ordem">{moeda2(o.frete.valor)}</dd>
          </div>
          <div>
            <dt>Margem após frete</dt>
            <dd>
              {moeda2(eco.aposFrete)} ({o.valores.venda ? pct(eco.aposFrete / o.valores.venda) : '—'})
            </dd>
          </div>
          <div>
            <dt>Frete total do Pedido</dt>
            <dd>{moeda2(o.freteTotalPedido)}</dd>
          </div>
          {Object.values(o.ptaxReferencias ?? {}).length > 0 && (
            <div>
              <dt>PTAX (USD → BRL)</dt>
              <dd className="valor-ptax">
                {Object.values(o.ptaxReferencias ?? {})
                  .map((x) => `${x.valor.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}${x.data ? ` (${dataBr(x.data)})` : ''}`)
                  .filter((x, i, xs) => xs.indexOf(x) === i)
                  .join(' · ')}
              </dd>
            </div>
          )}
        </dl>
      </LinhaSecao>

      <LinhaSecao titulo="Faturamentos" icone="fatura" teste="faturamentos-ordem">
        {o.faturamentos.length === 0 ? (
          <p className="vazio-linha">Nenhum faturamento registrado.</p>
        ) : (
          <div className="rolagem quadro-tabela">
            <table className="tabela tabela-compacta" data-teste="lista-faturamentos">
              <thead>
                <tr>
                  <th>NF</th>
                  <th>Status</th>
                  <th>Data e hora</th>
                  <th>Itens e quantidades</th>
                  <th className="numero">Valor</th>
                  <th>Registrado por</th>
                  <th className="col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {o.faturamentos.map((f) => (
                  <tr key={f.id} className={f.estorno ? 'lancamento-estornado' : undefined} data-teste={`faturamento-${f.nf}`}>
                    <td className="nowrap">
                      <strong>NF {f.nf}</strong>
                    </td>
                    <td>
                      {f.estorno ? (
                        <>
                          <SeloSituacao texto="Estornado" tom="encerrado" />
                          <div className="texto-secundario">
                            {quando(f.estorno.dataHora)} · {f.estorno.autor} · {f.estorno.justificativa}
                          </div>
                        </>
                      ) : (
                        <SeloSituacao texto="Ativo" tom="positivo" />
                      )}
                    </td>
                    <td className="nowrap">{quando(f.dataHora)}</td>
                    <td>{f.itens.map((i) => `${produtoDe(pedido, i.itemId)?.sku || produtoDe(pedido, i.itemId)?.nome || i.itemId} × ${i.quantidade}`).join('; ')}</td>
                    <td className="numero nowrap">{moeda2(f.valor)}</td>
                    <td>{f.autor}</td>
                    <td className="col-acoes">
                      {!f.estorno && pode('estornar_faturamento', 'ordem') && (
                        <button type="button" className="botao-secundario pequeno" onClick={() => setEstornar(f.id)} data-teste={`estornar-${f.nf}`}>
                          Estornar 🔒
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LinhaSecao>

      <Recolhivel titulo="Histórico" icone="relogio" teste="recolhivel-historico">
        <HistoricoLista itens={o.historico} />
      </Recolhivel>

      <div className="acoes-form">
        {ativa && o.saldo > 0 && pode('cancelar', 'ordem') && (
          <button type="button" className="botao-secundario" onClick={() => setModal('cancelar')} data-teste="cancelar-ordem">
            {o.situacao === 'faturada_parcialmente' ? 'Cancelar o saldo 🔒' : 'Cancelar 🔒'}
          </button>
        )}
        {o.cancelamento && !o.substituta && o.itens.some((i) => i.quantidade > i.faturada) && pode('gerar', 'ordem') && (
          <button type="button" className="botao-primario" onClick={() => setModal('substituir')} data-teste="criar-substituta">
            Criar Ordem substituta
          </button>
        )}
      </div>

      {modal === 'conferir' && <ModalConferirEmissaoOcf o={o} aoFechar={fechar} aoSalvar={salvar} aoEditarEntrega={() => setModal('entrega')} />}
      {modal === 'entrega' && <ModalDadosEntrega o={o} aoFechar={fechar} aoSalvar={salvar} />}
      {modal === 'envio' && <ModalRegistrarEnvio o={o} aoFechar={fechar} aoSalvar={salvar} />}
      {modal === 'confirmacao' && <ModalConfirmacaoFornecedor o={o} aoFechar={fechar} aoSalvar={salvar} />}
      {modal === 'pedido' && <ModalPedidoFornecedor o={o} aoFechar={fechar} aoSalvar={salvar} />}
      {modal === 'cancelar' && <ModalCancelar numero={o.numero} aoFechar={fechar} aoSalvar={salvar} />}
      {modal === 'faturar' && <ModalFaturamento o={o} aoFechar={fechar} aoSalvar={salvar} />}
      {estornar && (
        <ModalEstorno
          o={o}
          lancamentoId={estornar}
          aoFechar={() => setEstornar(null)}
          aoSalvar={(x) => {
            setEstornar(null);
            aplicar(x);
          }}
        />
      )}
      {modal === 'substituir' && (
        <ModalSubstituta
          numero={o.numero}
          entidadeAtual={o.entidade.id}
          aoFechar={fechar}
          aoCriar={(n) => {
            setModal(null);
            recarregar();
            navegar(`/ordens/${n}`);
          }}
        />
      )}
    </>
  );
}

function HistoricoLista({ itens }: { itens: Historico[] }) {
  return (
    <ul className="linha-tempo" data-teste="historico-ordem">
      {itens.map((h, i) => (
        <li key={i}>
          <strong>{quando(h.dataHora)}</strong> · {h.texto} <span className="texto-secundario">({h.autor})</span>
        </li>
      ))}
      {itens.length === 0 && <li className="texto-secundario">Sem registros.</li>}
    </ul>
  );
}

/** Ação com erro mostrado no próprio modal. */
function useEnvio(aoSalvar: (x: OcfCompleta) => void) {
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const executar = async (f: () => Promise<OcfCompleta>) => {
    if (enviando) return;
    setErro('');
    setEnviando(true);
    try {
      aoSalvar(await f());
    } catch (x) {
      setErro((x as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return { erro, enviando, executar };
}
const ErroModal = ({ erro }: { erro: string }) =>
  erro ? (
    <p className="aviso aviso-erro" role="alert" data-teste="erro-modal">
      {erro}
    </p>
  ) : null;

/** Dados operacionais da entrega (antes da emissão): contato, telefone e observações. */
function ModalDadosEntrega({ o, aoFechar, aoSalvar }: { o: OcfCompleta; aoFechar: () => void; aoSalvar: (x: OcfCompleta) => void }) {
  const [contato, setContato] = useState(o.contatoEntrega);
  const [telefone, setTelefone] = useState(o.telefoneEntrega);
  const [obs, setObs] = useState(o.observacoes);
  const { erro, enviando, executar } = useEnvio(aoSalvar);
  return (
    <Modal
      titulo="Dados de entrega da OCF"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={enviando} onClick={() => void executar(() => api.patch(`/api/ordens/${o.numero}/complemento`, { contato_entrega: contato.trim(), telefone_entrega: telefone.trim(), observacoes: obs.trim() }))} data-teste="salvar-complemento">
            Salvar dados de entrega
          </button>
        </>
      }
    >
      <div data-teste="conferencia-ordem">
        <ErroModal erro={erro} />
        <div className="form-grade grade-2">
          <label>
            Contato de entrega *
            <input value={contato} onChange={(e) => setContato(e.target.value)} maxLength={120} data-teste="conf-contato" />
          </label>
          <label>
            Telefone de entrega *
            <input value={telefone} onChange={(e) => setTelefone(e.target.value)} maxLength={40} data-teste="conf-telefone" />
          </label>
          <label className="largura-total">
            Observações operacionais
            <textarea rows={2} value={obs} onChange={(e) => setObs(e.target.value)} maxLength={2000} />
          </label>
        </div>
      </div>
    </Modal>
  );
}

/** D1: Conferir emissão da OCF (ref. 05) — prévia, checkbox, voltar sem gravar; o servidor revalida e não emite duas vezes. */
function ModalConferirEmissaoOcf({ o, aoFechar, aoSalvar, aoEditarEntrega }: { o: OcfCompleta; aoFechar: () => void; aoSalvar: (x: OcfCompleta) => void; aoEditarEntrega: () => void }) {
  const [conferi, setConferi] = useState(false);
  const { erro, enviando, executar } = useEnvio(aoSalvar);
  const pedido = o.pedidoCompleto;
  const c = pedido.conteudo;
  const cad = pedido.cadastro;
  const e = o.entidade;
  const pendencias = [!o.contatoEntrega.trim() && 'contato de entrega', !o.telefoneEntrega.trim() && 'telefone de entrega'].filter(Boolean) as string[];
  return (
    <Modal
      titulo="Conferir emissão da OCF"
      largo
      aoFechar={aoFechar}
      rodape={
        <>
          <label className="conferi-ordem">
            <input type="checkbox" checked={conferi} onChange={(x) => setConferi(x.target.checked)} disabled={pendencias.length > 0} data-teste="conferi-emissao" /> Conferi os dados para emissão
            <Ajuda titulo="O que a emissão faz">Ao confirmar, a OCF passa a Emitida: os dados operacionais ficam travados e o PDF fica definitivo. Nada é enviado ao fornecedor — o envio é registrado depois, em “Registrar envio”, com a data, o meio e a evidência. O servidor confere os dados de novo e não emite duas vezes.</Ajuda>
          </label>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar sem emitir
          </button>
          <button type="button" className="botao-primario" disabled={!conferi || pendencias.length > 0 || enviando} onClick={() => void executar(() => api.post(`/api/ordens/${o.numero}/emitir`, { conferido: true, revisao: o.revisao ?? 0 }))} data-teste="emitir-ordem">
            Confirmar emissão
          </button>
        </>
      }
    >
      <ErroModal erro={erro} />
      <div className="ctx-modal">
        <div>
          <h3>{o.numero} · Ordem de Compra ao Fornecedor</h3>
          <p>
            Pedido de origem: {pedido.numero} · Proposta {pedido.proposta.numero} v{pedido.versao}
          </p>
        </div>
        <SeloSituacao texto="Em conferência" tom="backlog" />
      </div>
      {pendencias.length > 0 && (
        <p className="aviso aviso-alerta" data-teste="motivo-bloqueio-emitir">
          Para emitir, informe: {pendencias.join(' e ')}.{' '}
          <button type="button" className="botao-link" onClick={aoEditarEntrega}>
            Editar dados de entrega
          </button>
        </p>
      )}
      <CardsLista n={3}>
        <CardLista icone="pedidos" tom="azul" rotulo="Valor da Ordem" valor={moeda2(o.valores.venda)} teste="conf-valor" />
        <CardLista icone="documento" tom="roxo" rotulo="Itens" valor={o.itens.length} detalhe={`Qtd. total: ${o.progresso.total}`} teste="conf-itens" />
        <CardLista icone="relogio" tom="verde" rotulo="Situação" valor="Aguardando emissão" teste="conf-situacao" />
      </CardsLista>
      <Par>
        <QuadroCampos
          titulo="Parceiro"
          icone="predio"
          campos={[
            ['Entidade faturadora', e.razao],
            ['CNPJ', e.cnpj],
            ['Código de revenda', e.codigoRevenda],
            ['Contato', (e.contato || e.email || e.telefone) && <Empilha itens={[e.contato, e.email, e.telefone].filter(Boolean) as ReactNode[]} />],
          ]}
        />
        <QuadroCampos
          titulo="Inttrix"
          icone="predio"
          tom="neutro"
          campos={[
            ['Razão social', c.empresa?.razao ?? ''],
            ['CNPJ', c.empresa?.cnpj ?? ''],
            ['Responsável', pedido.responsavel.nome],
            ['E-mail', pedido.responsavel.email],
          ]}
        />
      </Par>
      <Par>
        <QuadroLocal
          titulo="Faturar ao cliente final"
          icone="predio"
          tom="turquesa"
          razao={c.faturamento.razao}
          linhas={[
            <Junta partes={[`CNPJ ${c.faturamento.cnpj}`, <TextoHistorico rotulo="IE" v={cad.faturamento.ie} dataReferencia={cad.dataReferencia} />]} />,
            enderecoTexto(c.faturamento.endereco),
            <Junta partes={[c.faturamento.contato && `Contato: ${c.faturamento.contato}`, <TextoHistorico rotulo="E-mail" v={cad.faturamento.email} dataReferencia={cad.dataReferencia} />, c.faturamento.telefone]} />,
          ]}
        />
        <QuadroLocal
          titulo="Entregar em"
          icone="caminhao"
          tom="azul"
          razao={c.entrega.razao}
          linhas={[
            <Junta partes={[`CNPJ ${c.entrega.cnpj}`, <TextoHistorico rotulo="IE" v={cad.entrega.ie} dataReferencia={cad.dataReferencia} />]} />,
            enderecoTexto(c.entrega.endereco),
            <Junta partes={[`Contato: ${o.contatoEntrega || '— pendente'}`, o.telefoneEntrega || '— pendente']} />,
          ]}
        />
      </Par>
      <FaixaCondicoes titulo="Condições comerciais" itens={[['Pagamento', pagamentoTexto(c.condicoes)], ['Frete', `${o.frete.modalidade} · ${PAGADOR_CURTO[o.frete.pagador]}`], ['OC do Cliente', pedido.ocsVigentes.map((x) => x.numero).join(' · ') || 'Não informada']]} />
      <h2 className="titulo-tab">Itens da Ordem</h2>
      <div className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta" data-teste="conf-itens-ordem">
          <thead>
            <tr>
              <th>PN</th>
              <th>Descrição</th>
              <th className="numero">Qtd.</th>
              <th className="numero">Custo unitário</th>
              <th className="numero">Custo total</th>
              <th className="numero">Venda unitária</th>
              <th className="numero">Venda total</th>
              <th className="numero">Margem</th>
            </tr>
          </thead>
          <tbody>
            {o.itens.map((i) => {
              const pr = produtoDe(pedido, i.itemId);
              const m = (i.precoUnit - i.custoUnit) * i.quantidade;
              return (
                <tr key={i.itemId}>
                  <td className="nowrap">{pr?.sku || '—'}</td>
                  <td>{pr?.nome ?? i.itemId}</td>
                  <td className="numero">{i.quantidade}</td>
                  <td className="numero nowrap">{moeda2(i.custoUnit)}</td>
                  <td className="numero nowrap">{moeda2(i.custoUnit * i.quantidade)}</td>
                  <td className="numero nowrap">{moeda2(i.precoUnit)}</td>
                  <td className="numero nowrap">{moeda2(i.precoUnit * i.quantidade)}</td>
                  <td className="numero nowrap">
                    {moeda2(m)}
                    <br />
                    {pct(i.precoUnit * i.quantidade ? m / (i.precoUnit * i.quantidade) : 0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

/** D1: registro manual do envio feito fora do HUB — nada é enviado; data e hora efetivas; evidência opcional. */
function ModalRegistrarEnvio({ o, aoFechar, aoSalvar }: { o: OcfCompleta; aoFechar: () => void; aoSalvar: (x: OcfCompleta) => void }) {
  const [dataHora, setDataHora] = useState(agora());
  const [meio, setMeio] = useState<'email' | 'portal' | 'outro'>('email');
  const [destinatario, setDestinatario] = useState('');
  const [obs, setObs] = useState('');
  const [evidencia, setEvidencia] = useState('');
  // evidência: documentos ativos da Oportunidade (restritos a quem não pode vê-los não aparecem)
  const docs = useCarga(() => api.get<{ documentos: { id: string; nome: string; situacao: string; oculto?: boolean }[] }>(`/api/oportunidades/${o.pedidoCompleto.oportunidade.id}/documentos`).then((r) => r.documentos.filter((d) => !d.oculto)).catch(() => []), [o.pedidoCompleto.oportunidade.id]);
  const { erro, enviando, executar } = useEnvio(aoSalvar);
  return (
    <Modal
      titulo="Registrar envio ao fornecedor"
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={enviando || !dataHora || (meio === 'outro' && !obs.trim())} onClick={() => void executar(() => api.post(`/api/ordens/${o.numero}/envio`, { data_hora: dataHora, meio, destinatario: destinatario.trim(), observacao: obs.trim(), evidencia_documento_id: evidencia || undefined }))} data-teste="confirmar-envio">
            Registrar envio
          </button>
        </>
      }
    >
      <ErroModal erro={erro} />
      <div className="ctx-modal">
        <div>
          <h3>
            {o.numero} · {o.entidade.razao}
          </h3>
          <p>
            Registro manual do envio feito por você
            <Ajuda titulo="Registrar envio">O HUB não envia e-mail nem mensagem ao fornecedor. Emitir ou baixar o PDF não conta como envio: registre aqui a data e a hora em que a Ordem foi efetivamente enviada, o meio e, se quiser, o destinatário e um documento como evidência. O registro fica no histórico e não muda depois.</Ajuda>
          </p>
        </div>
      </div>
      {/* achado HML (18/10): rótulos curtos, campos na mesma base, data e hora inteiras com os controles */}
      <div className="form-envio" data-teste="form-envio">
        <div className="linha-envio l3">
          <CampoData rotulo="Data do envio" valor={dataHora.split('T')[0] ?? ''} aoMudar={(d) => setDataHora(d ? `${d}T${dataHora.split('T')[1] || '09:00'}` : '')} teste="envio-data-hora" obrigatorio />
          <label>
            Hora *
            <input type="time" value={dataHora.split('T')[1] ?? ''} onChange={(x) => setDataHora(`${dataHora.split('T')[0] || hojeIso()}T${x.target.value}`)} data-teste="envio-data-hora-hora" />
          </label>
          <label>
            Meio *
            <select value={meio} onChange={(x) => setMeio(x.target.value as 'email' | 'portal' | 'outro')} data-teste="envio-meio">
              <option value="email">E-mail</option>
              <option value="portal">Portal do fornecedor</option>
              <option value="outro">Outro (descreva na observação)</option>
            </select>
          </label>
        </div>
        <div className="linha-envio l2">
          <label>
            Enviado para
            <input value={destinatario} onChange={(x) => setDestinatario(x.target.value)} maxLength={200} placeholder="pedidos@fornecedor…" data-teste="envio-destinatario" />
          </label>
          <label>
            Evidência (documento da Oportunidade)
            <select value={evidencia} onChange={(x) => setEvidencia(x.target.value)} data-teste="envio-evidencia">
              <option value="">— nenhuma —</option>
              {(docs.dados ?? []).filter((d) => d.situacao === 'ativo').map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nome}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label>
          Observação{meio === 'outro' ? ' *' : ''}
          <textarea rows={2} value={obs} onChange={(x) => setObs(x.target.value)} maxLength={1000} data-teste="envio-observacao" />
        </label>
      </div>
    </Modal>
  );
}

/** D1: confirmação de recebimento pelo fornecedor, com o número do Pedido do fornecedor (opcional; pode vir depois). */
function ModalConfirmacaoFornecedor({ o, aoFechar, aoSalvar }: { o: OcfCompleta; aoFechar: () => void; aoSalvar: (x: OcfCompleta) => void }) {
  const [data, setData] = useState(hojeIso());
  const [numero, setNumero] = useState('');
  const [obs, setObs] = useState('');
  const { erro, enviando, executar } = useEnvio(aoSalvar);
  return (
    <Modal
      titulo="Registrar confirmação do fornecedor"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={enviando || !data} onClick={() => void executar(() => api.post(`/api/ordens/${o.numero}/marcos`, { marco: 'confirmada', data, pedido_fornecedor: numero.trim(), observacao: obs.trim() }))} data-teste="salvar-confirmacao">
            Registrar confirmação
          </button>
        </>
      }
    >
      <ErroModal erro={erro} />
      <div className="ctx-modal">
        <div>
          <h3>
            {o.numero} · {o.entidade.razao}
          </h3>
          <p>
            Confirmação de recebimento pelo fornecedor
            <Ajuda titulo="Confirmação do fornecedor">Registra que o fornecedor confirmou o recebimento da Ordem. O número do Pedido do fornecedor (no sistema dele) pode ser informado agora ou depois, com a mesma permissão; cada alteração fica no histórico. É diferente do nosso PED-, da OCF e da OC do Cliente.</Ajuda>
          </p>
        </div>
      </div>
      <div className="form-grade grade-2">
        <CampoData rotulo="Data da confirmação *" valor={data} aoMudar={setData} teste="confirmacao-data" />
        <label>
          Pedido do fornecedor
          <input value={numero} onChange={(x) => setNumero(x.target.value)} maxLength={60} placeholder="Pode informar depois" data-teste="confirmacao-pedido-fornecedor" />
        </label>
        <label className="largura-total">
          Observação
          <textarea rows={2} value={obs} onChange={(x) => setObs(x.target.value)} maxLength={1000} />
        </label>
      </div>
    </Modal>
  );
}

/** Informar ou atualizar o Pedido do fornecedor (mesma permissão da confirmação; auditado). */
function ModalPedidoFornecedor({ o, aoFechar, aoSalvar }: { o: OcfCompleta; aoFechar: () => void; aoSalvar: (x: OcfCompleta) => void }) {
  const [numero, setNumero] = useState(o.confirmada?.pedidoFornecedor ?? '');
  const { erro, enviando, executar } = useEnvio(aoSalvar);
  return (
    <Modal
      titulo="Pedido do fornecedor"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={enviando || !numero.trim() || numero.trim() === (o.confirmada?.pedidoFornecedor ?? '')} onClick={() => void executar(() => api.patch(`/api/ordens/${o.numero}/pedido-fornecedor`, { pedido_fornecedor: numero.trim() }))} data-teste="salvar-pedido-fornecedor">
            Salvar
          </button>
        </>
      }
    >
      <ErroModal erro={erro} />
      <label>
        Número do Pedido no sistema do fornecedor
        <input value={numero} onChange={(x) => setNumero(x.target.value)} maxLength={60} data-teste="pedido-fornecedor-numero" />
      </label>
      <p className="nota-discreta">A alteração fica no histórico da Ordem, com autor e data.</p>
    </Modal>
  );
}

/** Registrar faturamento: por item e quantidade (≤ saldo), NF, data e hora efetivas. O estado é recalculado. */
function ModalFaturamento({ o, aoFechar, aoSalvar }: { o: OcfCompleta; aoFechar: () => void; aoSalvar: (x: OcfCompleta) => void }) {
  const [qtd, setQtd] = useState<Record<string, string>>({});
  const [nf, setNf] = useState('');
  const [dataHora, setDataHora] = useState(agora());
  const [obs, setObs] = useState('');
  const [tentou, setTentou] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const nome = (itemId: string) => produtoDe(o.pedidoCompleto, itemId)?.nome ?? itemId;
  const linhas = o.itens.map((i) => ({ i, saldo: i.quantidade - i.faturada, q: Number(qtd[i.itemId] || 0) }));
  const faltam = [
    !nf.trim() && 'Informe o número da NF.',
    !dataHora && 'Informe a data e a hora do faturamento.',
    dataHora && dataHora > agora() && 'A data do faturamento não pode ser futura.',
    linhas.every((l) => !l.q) && 'Informe a quantidade faturada de ao menos um item.',
    ...linhas.filter((l) => l.q && (l.q < 0 || !Number.isInteger(l.q))).map((l) => `${nome(l.i.itemId)}: use um número inteiro positivo.`),
    ...linhas.filter((l) => l.q > l.saldo).map((l) => `${nome(l.i.itemId)}: a quantidade (${l.q}) passa do saldo (${l.saldo}).`),
  ].filter(Boolean) as string[];
  const confirmar = async () => {
    setTentou(true);
    if (faltam.length) return;
    setErro('');
    setSalvando(true);
    try {
      aoSalvar(await api.post<OcfCompleta>(`/api/ordens/${o.numero}/faturamentos`, { nf: nf.trim(), data_hora: dataHora, itens: linhas.filter((l) => l.q > 0).map((l) => ({ itemId: l.i.itemId, quantidade: l.q })), observacao: obs }));
    } catch (x) {
      setErro((x as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Registrar faturamento — ${o.numero}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={() => void confirmar()} disabled={salvando} data-teste="confirmar-faturamento">
            Registrar faturamento
          </button>
        </>
      }
    >
      <p className="texto-secundario">O faturamento é um evento: registra NF, data e hora efetivas e as quantidades por item. O estado da Ordem (Backlog, Faturada parcialmente, Faturada) é calculado a partir daqui.</p>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <div className="rolagem">
        <table className="tabela tabela-compacta" data-teste="itens-faturamento">
          <thead>
            <tr>
              <th>Item</th>
              <th>Qtd da Ordem</th>
              <th>Já faturada</th>
              <th>Saldo</th>
              <th>Faturar agora</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(({ i, saldo }) => (
              <tr key={i.itemId}>
                <td className="descricao-produto">{nome(i.itemId)}</td>
                <td>{i.quantidade}</td>
                <td>{i.quantidade - saldo}</td>
                <td>{saldo}</td>
                <td>
                  <input type="number" min={0} max={saldo} step={1} value={qtd[i.itemId] ?? ''} disabled={saldo === 0} onChange={(e) => setQtd({ ...qtd, [i.itemId]: e.target.value })} aria-label={`Faturar agora — ${nome(i.itemId)}`} className="entrada-curta" data-teste={`faturar-${i.itemId}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grade-form sem-borda">
        <label>
          Número da NF *
          <input value={nf} onChange={(e) => setNf(e.target.value)} maxLength={40} data-teste="nf-faturamento" />
        </label>
        <div className="coluna-dupla">
          <CampoDataHora rotulo="Faturada em" valor={dataHora} aoMudar={setDataHora} teste="data-faturamento" obrigatorio />
        </div>
        <label className="largura-total">
          Observação
          <input value={obs} onChange={(e) => setObs(e.target.value)} maxLength={1000} />
        </label>
      </div>
      {tentou && faltam.length > 0 && <Estado tipo="bloqueado" titulo="Falta completar" itens={faltam} teste="faltam-faturamento" />}
    </Modal>
  );
}

/** Estorno 🔒: nunca apaga o lançamento; exige justificativa e devolve as quantidades ao saldo. */
function ModalEstorno({ o, lancamentoId, aoFechar, aoSalvar }: { o: OcfCompleta; lancamentoId: string; aoFechar: () => void; aoSalvar: (x: OcfCompleta) => void }) {
  const f = o.faturamentos.find((x) => x.id === lancamentoId)!;
  const [just, setJust] = useState('');
  const [erro, setErro] = useState('');
  const ok = just.trim().length >= 4;
  const confirmar = async () => {
    setErro('');
    try {
      aoSalvar(await api.post<OcfCompleta>(`/api/ordens/${o.numero}/faturamentos/${f.id}/estorno`, { justificativa: just.trim() }));
    } catch (x) {
      setErro((x as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Estornar faturamento NF ${f.nf} — ${o.numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!ok} onClick={() => void confirmar()} data-teste="confirmar-estorno">
            Confirmar estorno
          </button>
        </>
      }
    >
      <p className="aviso aviso-info">
        O lançamento da NF {f.nf} ({f.itens.reduce((s, i) => s + i.quantidade, 0)} unidade(s), {moeda2(f.valor)}) continua no histórico, marcado como estornado. As quantidades voltam ao saldo e o estado da Ordem é recalculado. Fica na auditoria e na Timeline.
      </p>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <label className="campo">
        Justificativa (obrigatória)
        <input value={just} onChange={(e) => setJust(e.target.value)} maxLength={1000} data-teste="justificativa-estorno" />
      </label>
    </Modal>
  );
}

function ModalCancelar({ numero, aoFechar, aoSalvar }: { numero: string; aoFechar: () => void; aoSalvar: (x: OcfCompleta) => void }) {
  const opcoes = useCarga(() => api.get<OpcoesOrdens>('/api/ordens/opcoes'), []);
  const [motivo, setMotivo] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState('');
  const confirmar = async () => {
    setErro('');
    try {
      aoSalvar(await api.post<OcfCompleta>(`/api/ordens/${numero}/cancelar`, { motivo, observacao: obs.trim() }));
    } catch (x) {
      setErro((x as Error).message);
    }
  };
  const motivos = opcoes.dados?.motivos_cancelamento ?? [];
  const exigeObs = motivoExigeObservacao(motivo); // "Outro" exige a Observação
  return (
    <Modal
      titulo={`Cancelar ${numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!motivo || (exigeObs && !obs.trim())} onClick={() => void confirmar()} data-teste="confirmar-cancelar-ordem">
            Confirmar cancelamento
          </button>
        </>
      }
    >
      <p className="aviso aviso-info">O cancelamento é definitivo: a Ordem fica Cancelada para sempre, com motivo, data, hora e autoria. Só o SALDO não faturado é cancelado e liberado; o que já foi faturado fica preservado e continua nos realizados.</p>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      {opcoes.dados && motivos.length === 0 && <p className="aviso aviso-alerta">A lista de motivos de cancelamento não está configurada neste ambiente (Parâmetros).</p>}
      <label className="campo">
        Motivo (obrigatório)
        <select value={motivo} onChange={(e) => setMotivo(e.target.value)} data-teste="motivo-cancelar-ordem">
          <option value="">Selecione…</option>
          {motivos.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
      </label>
      <label className="campo">
        {exigeObs ? 'Observação (obrigatória para o motivo “Outro”)' : 'Observação'}
        <textarea rows={3} value={obs} onChange={(e) => setObs(e.target.value)} maxLength={1000} required={exigeObs} data-teste="observacao-cancelar-ordem" />
      </label>
    </Modal>
  );
}

function ModalSubstituta({ numero, entidadeAtual, aoFechar, aoCriar }: { numero: string; entidadeAtual: string; aoFechar: () => void; aoCriar: (numero: string) => void }) {
  const opcoes = useCarga(() => api.get<OpcoesOrdens>('/api/ordens/opcoes'), []);
  const [entidade, setEntidade] = useState('');
  const [erro, setErro] = useState('');
  const ativas = opcoes.dados?.entidades ?? [];
  const escolhida = entidade || ativas.find((e) => e.id !== entidadeAtual)?.id || '';
  const grupos = [...new Map(ativas.map((e) => [e.grupoId, e.grupo])).entries()];
  const criar = async () => {
    setErro('');
    try {
      aoCriar((await api.post<{ numero: string }>(`/api/ordens/${numero}/substituta`, { entidade_id: escolhida })).numero);
    } catch (x) {
      setErro((x as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Criar Ordem substituta de ${numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!escolhida} onClick={() => void criar()} data-teste="confirmar-substituta">
            Criar substituta
          </button>
        </>
      }
    >
      <p className="aviso aviso-info">A substituta é uma nova Ordem, em Backlog, com a quantidade não faturada. A {numero} continua Cancelada e passa a mostrar o rótulo “Substituída”.</p>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <label className="campo">
        Entidade faturadora
        <select value={escolhida} onChange={(e) => setEntidade(e.target.value)} data-teste="entidade-substituta">
          {grupos.map(([gid, gnome]) => (
            <optgroup key={gid} label={gnome}>
              {ativas
                .filter((e) => e.grupoId === gid)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.fantasia} — {e.cnpj}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </label>
    </Modal>
  );
}

/* =============================== OL / OTI =============================== */

interface SecaoDoc {
  titulo: string;
  teste: string;
  pares?: [string, string][];
  tabela?: ReactNode;
  alerta?: string;
}

// P3-24: a OL não mostra Modelo; "Observação técnica" só aparece quando um registro antigo a tiver (dado preservado)
const TabelaComponentesOL = ({ c }: { c: ConteudoPedido }) => {
  const comObs = c.locacao!.componentes.some((x) => x.observacaoTecnica);
  return (
    <table className="tabela tabela-compacta folha-tabela" data-teste="componentes-documento">
      <thead>
        <tr>
          <th>PN/SKU</th>
          <th>Descrição</th>
          <th>Fabricante</th>
          <th>Pilar</th>
          <th>Tipo</th>
          <th>Qtd ganha</th>
          {comObs && <th>Observação técnica</th>}
        </tr>
      </thead>
      <tbody>
        {c.locacao!.componentes.map((x) => (
          <tr key={x.id}>
            <td>{x.pn || '—'}</td>
            <td>{x.descricao}</td>
            <td>{x.fabricante || '—'}</td>
            <td>{x.pilar || '—'}</td>
            <td>{x.tipoItem || '—'}</td>
            <td>{x.quantidadeAceita}</td>
            {comObs && <td>{x.observacaoTecnica || '—'}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const TabelaInventarioOTI = ({ c }: { c: ConteudoPedido }) => (
  <table className="tabela tabela-compacta folha-tabela folha-tabela-larga" data-teste="inventario-documento">
    <thead>
      <tr>
        <th>Categoria</th>
        <th>Fabricante</th>
        <th>Modelo</th>
        <th>PN</th>
        <th>Serial/lote</th>
        <th>Condição</th>
        <th>Características</th>
        <th>Funcional</th>
        <th>Acessórios</th>
        <th>Qtd ganha</th>
      </tr>
    </thead>
    <tbody>
      {c.tradein!.equipamentos.map((e) => (
        <tr key={e.id}>
          <td>{e.categoria}</td>
          <td>{e.fabricante}</td>
          <td>{e.modelo}</td>
          <td>{e.pn || '—'}</td>
          <td>{e.serial || e.lote || '—'}</td>
          <td>{e.condicao || '—'}</td>
          <td>{[e.processador, e.hd, e.storage, e.memoria, e.tela].filter(Boolean).join(' · ') || '—'}</td>
          <td>{(e as { funcional?: string }).funcional || '—'}</td>
          <td>{(e as { acessorios?: string }).acessorios || '—'}</td>
          <td>{e.quantidadeAceita}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const TabelaLinhasTradein = ({ c }: { c: ConteudoPedido }) => (
  <table className="tabela tabela-compacta folha-tabela" data-teste="linhas-tradein">
    <thead>
      <tr>
        <th>Equipamento/lote</th>
        <th>Qtd ganha</th>
        <th>Avaliação unitária</th>
        <th>Avaliação total</th>
        <th>Unitário ao Cliente</th>
        <th>Total ao Cliente</th>
        <th>Comissão</th>
      </tr>
    </thead>
    <tbody>
      {c.tradein!.linhas.map((l) => {
        const e = c.tradein!.equipamentos.find((x) => x.id === l.id);
        return (
          <tr key={l.id}>
            <td>
              {l.descricao}
              {e?.lote ? <div className="texto-secundario">{e.lote}</div> : null}
            </td>
            <td>{l.quantidade}</td>
            <td>{moeda2(l.unitBruto)}</td>
            <td>{moeda2(l.totalBruto)}</td>
            <td>{moeda2(l.unitCliente)}</td>
            <td>{moeda2(l.totalCliente)}</td>
            <td>{moeda2(l.comissao)}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

/**
 * Seções dos documentos — OL: origem → Cliente e local → Parceiro de Locação → itens → condições financeiras →
 * condições contratuais → logística e implantação → OC → observações. OTI: origem → Cliente e local → Comprador →
 * inventário → valores → logística → condições → confirmação dos dados bancários (os dados ficam fora do HUB) → OC.
 * Condições ACEITAS (congeladas). A comissão BRUTA acordada pode aparecer; a comissão líquida estimada (interna), nunca.
 */
function secoesDocumento(doc: DocCompleto): SecaoDoc[] {
  const p = doc.pedidoCompleto;
  const c = p.conteudo;
  const e = doc.entidade;
  const ocs = p.ocsVigentes.map((x) => `${x.numero} (${x.tipo}, ${x.dataRecebimento ? `recebida em ${dataBr(x.dataRecebimento)}` : 'data de recebimento não informada na origem'})`).join(' · ') || 'pendente — Cliente ainda não enviou';
  const origem: SecaoDoc = { titulo: '1. ORIGEM E REFERÊNCIAS', teste: 'doc-origem', pares: [['Documento', `${doc.numero} — ${NOME_DOCUMENTO[doc.tipo]}`], ['Situação', doc.situacaoTexto], ['Pedido', p.numero], ['Proposta e versão aceita', `${p.proposta.numero} | V${p.versao}`], ['Oportunidade', `#${p.oportunidade.numero}`], ['Data', dataBr(doc.dataCriacao)]] };
  const parceiroPares: [string, string][] = [['Parceiro comercial', e?.grupo ?? '—'], ['Entidade/CNPJ', e ? `${e.razao} — ${e.cnpj}` : '—'], ['Contato do parceiro', e ? [e.contato, e.email, e.telefone].filter(Boolean).join(' · ') || '—' : '—']];
  if (doc.tipo === 'locacao') {
    const L = c.locacao!;
    const cc = L.condicoes;
    const regra = L.regraComissao;
    return [
      origem,
      { titulo: '2. CLIENTE E LOCAL', teste: 'doc-cliente', pares: [['Cliente', p.cliente.nome], ['Entidade/CNPJ do Cliente', `${p.cliente.razao} — ${p.cliente.cnpj}`], ['Endereço de instalação', enderecoTexto(c.entrega.endereco)], ['Contato responsável', c.entrega.contato || '—'], ['Telefone', c.entrega.telefone || '—']] },
      { titulo: '3. PARCEIRO DE LOCAÇÃO E ENTIDADE/CNPJ', teste: 'doc-parceiro', pares: parceiroPares },
      { titulo: '4. ITENS E COMPONENTES', teste: 'doc-itens', tabela: <TabelaComponentesOL c={c} /> },
      {
        titulo: '5. CONDIÇÕES FINANCEIRAS',
        teste: 'doc-financeiro',
        pares: [
          ['Valor da parcela (Cliente)', moeda2(L.clienteParcela)],
          ['Periodicidade · prazo · cobranças', `${L.periodicidade} · ${L.prazoMeses} meses · ${L.cobrancas}`],
          ['Valor total do contrato', moeda2(L.totalContrato)],
          ['Vencimentos', L.vencimentoInicial ? `${dataBr(L.vencimentoInicial)}${L.diaCobranca ? ` · dia ${L.diaCobranca}` : ''}` : 'Conforme a regra do parceiro, a partir da emissão de cada NF de remessa'],
          ['Forma de pagamento', L.formaPagamento],
          ['Regra de margem', regra ? `${regraComissao(regra.regra).nome} · ${pctNum(regra.percentual)} (base: ${regraComissao(regra.regra).base.toLowerCase()}; ${regraComissao(regra.regra).frequencia.toLowerCase()})` : `${pctNum(L.percentual)} sobre ${L.base === 'parcela' ? 'a parcela' : 'o total'}`],
          ['Comissão bruta', `${moeda2(L.comissaoParcela)} por cobrança · ${moeda2(L.comissaoTotal)} no contrato`],
          ['Líquido do parceiro', `${moeda2(L.liquidoParcela)} por cobrança · ${moeda2(L.liquidoTotal)} no contrato`],
        ],
      },
      { titulo: '6. CONDIÇÕES CONTRATUAIS (aceitas na versão)', teste: 'doc-contratuais', pares: [['Reajuste', textoReajuste(L.reajuste)], ['Renovação', cc?.renovacao || '—'], ['Cancelamento', cc?.cancelamento || '—'], ['Manutenção', cc?.manutencao || '—'], ['Substituição', cc?.substituicao || '—'], ['Seguro', cc?.seguro || 'não se aplica'], ['Responsabilidades', cc?.responsabilidades || '—'], ['Observações contratuais', cc?.observacoes || '—']] },
      { titulo: '7. LOGÍSTICA E IMPLANTAÇÃO', teste: 'doc-logistica', pares: [['Responsável pela entrega', L.responsavelEntrega ?? 'A definir'], ['Responsável pela implantação', L.responsavelImplantacao ?? 'A definir'], ['Prazo de implantação', L.implantacao || '—'], ['Implantação prevista', L.dataImplantacao ? dataBr(L.dataImplantacao) : '—'], ['Início da locação', L.dataInicial ? `${dataBr(L.dataInicial)} · término estimado ${dataBr(L.dataFinal)}` : 'Conforme a emissão de cada NF de remessa']] },
      { titulo: '8. OC DO CLIENTE', teste: 'doc-oc', pares: [['Referência', ocs]] },
      { titulo: '9. OBSERVAÇÕES E PENDÊNCIAS', teste: 'doc-pendencias', pares: [['Observações', doc.observacoes || L.observacoes || '—']], alerta: 'Aceite formal, reconhecimento da comissão e ciclo de vida da OL dependem de contrato próprio (próxima fase).' },
    ];
  }
  const T = c.tradein!;
  const conf = T.confirmacaoBancaria;
  return [
    origem,
    { titulo: '2. CLIENTE E LOCAL', teste: 'doc-cliente', pares: [['Cliente', p.cliente.nome], ['Entidade/CNPJ do Cliente', `${p.cliente.razao} — ${p.cliente.cnpj}`], ['Inscrição estadual', textoHistorico(p.cadastro.faturamento.ie, p.cadastro.dataReferencia)], ['Local do parque (congelado)', T.localCongelado?.texto ?? T.localParque], ['Origem do endereço', T.localCongelado?.origem ?? 'texto livre'], ['Contato responsável pela retirada', T.contatoLocal ? `${T.contatoLocal.nome} · ${T.contatoLocal.telefone} · ${T.contatoLocal.email}` : '—'], ['Agendamento', T.agendamento ? `${dataBr(T.agendamento.slice(0, 10))} às ${T.agendamento.slice(11, 16)}` : '—']] },
    { titulo: '3. COMPRADOR DE TRADE-IN', teste: 'doc-parceiro', pares: parceiroPares },
    { titulo: '4. INVENTÁRIO DETALHADO (quantidades ganhas; um lote por linha)', teste: 'doc-inventario', tabela: <TabelaInventarioOTI c={c} /> },
    { titulo: '5. VALORES POR LINHA', teste: 'doc-valores', tabela: <TabelaLinhasTradein c={c} />, pares: [['Avaliação bruta total', moeda2(T.bruto)], ['Valor total destinado ao Cliente', moeda2(T.oferta)], ['Comissão INTTRIX acordada com o comprador (bruta)', `${moeda2(T.comissao)} (${pctNum(T.percentualEquivalente)})`]] },
    { titulo: '6. LOGÍSTICA', teste: 'doc-logistica', pares: [['Modalidade', T.modalidadeLogistica || 'a definir'], ['Responsável pela logística', T.responsavelLogistica ?? 'A definir'], ['Prazo para retirada', T.prazoRetirada ? dataBr(T.prazoRetirada) : `${T.prazoRetiradaDias} dias`]] },
    { titulo: '7. INSPEÇÃO, ACEITE E RESPONSABILIDADES', teste: 'doc-condicoes', pares: [['Inspeção e aceite', T.condicoesOperacao || '—'], ['Responsabilidades e preparação', T.preparacao || T.responsabilidades || '—']] },
    { titulo: '8. DADOS BANCÁRIOS DO FAVORECIDO', teste: 'doc-bancario', pares: [['Confirmação', conf ? `Dados bancários confirmados por ${conf.usuario} em ${dataBr(conf.dataHora.slice(0, 10))} às ${conf.dataHora.slice(11, 16)} (os dados ficam fora do HUB)` : 'NÃO confirmados — emissão definitiva bloqueada']] },
    { titulo: '9. OC DO CLIENTE, OBSERVAÇÕES E PENDÊNCIAS', teste: 'doc-pendencias', pares: [['OC do Cliente', ocs], ['Observações', doc.observacoes || T.observacoes || '—']], alerta: 'Transferência de propriedade e pagamento ao Cliente dependem de contrato próprio (próxima fase).' },
  ];
}

function DocumentoOperacional({ doc, aplicar }: { doc: DocCompleto; aplicar: (x: DocCompleto) => void }) {
  const { pode } = useSessao();
  const [cancelar, setCancelar] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const p = doc.pedidoCompleto;
  const bloqueio = doc.tipo === 'trade_in' && !p.conteudo.tradein?.confirmacaoBancaria ? ['Confirme os dados bancários na Proposta (os dados ficam fora do HUB).'] : [];
  const acao = async (f: () => Promise<DocCompleto>) => {
    setErro('');
    try {
      aplicar(await f());
      setCancelar(false);
    } catch (x) {
      setErro((x as Error).message);
    }
  };
  return (
    <>
      <section className="resumo-destaque" data-teste="cabecalho-documento">
        <div className="cabecalho-registro">
          <h1 className="titulo-pagina">
            {NOME_DOCUMENTO[doc.tipo]} {doc.numero}
          </h1>
          <div className="selos-cabecalho">
            <SeloOrdem tipo={doc.tipo} />
            <SeloSituacao texto={doc.situacaoTexto} tom={tomDocumento(doc.situacao)} />
            <BotaoExcluir entidade="documento_operacional" id={doc.id} aoExcluir={depoisDeExcluir(`/pedidos/${p.numero}`)} />
          </div>
        </div>
        <p className="texto-secundario sem-margem" data-teste="origem-documento">
          Pedido <a href={`#/pedidos/${p.numero}`}>{p.numero}</a> <SeloTipo tipo={p.tipo} /> · Cliente {p.cliente.nome} · Criada em {dataBr(doc.dataCriacao)}
          {doc.emitida && ` · Emitida em ${quando(doc.emitida.dataHora)} por ${doc.emitida.autor}`}
          {doc.cancelamento && ` · Cancelada em ${quando(doc.cancelamento.dataHora)} por ${doc.cancelamento.autor} (motivo: ${doc.cancelamento.motivo})`}
        </p>
        <div className="acoes-direita barra-acoes-registro">
          <a className="botao-secundario" href={`#/ordens/${doc.numero}/pdf`} data-teste="pdf-documento">
            Visualizar PDF
          </a>
          {doc.situacao !== 'cancelada' && pode('cancelar', 'ordem') && (
            <button type="button" className="botao-secundario" onClick={() => setCancelar(true)} data-teste="cancelar-documento">
              Cancelar 🔒
            </button>
          )}
          {doc.situacao === 'em_conferencia' && pode('editar', 'ordem') && (
            <button type="button" className="botao-primario" disabled={bloqueio.length > 0} title={bloqueio.join(' ') || undefined} onClick={() => void acao(() => api.post(`/api/ordens/${doc.numero}/emitir`, {}))} data-teste="emitir-documento">
              Conferir e emitir
            </button>
          )}
        </div>
      </section>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      {doc.situacao === 'em_conferencia' && (bloqueio.length ? <Estado tipo="bloqueado" titulo="Emissão definitiva bloqueada" itens={bloqueio} teste="bloqueio-emissao" /> : <Estado tipo="concluido" titulo="Dados completos: confira e emita" teste="bloqueio-emissao" />)}
      <p className="aviso aviso-info">Documento operacional (nome e prefixo {doc.tipo === 'locacao' ? 'OL-' : 'OTI-'} provisórios). Não é Ordem de Compra ao Fornecedor. Dados do Pedido congelado — só linhas e quantidades ganhas, condições aceitas na versão.</p>
      <div className="secoes-documento" data-teste="dados-documento">
        {secoesDocumento(doc).map((s) => (
          <section key={s.teste} className="cartao secao-documento" data-teste={s.teste}>
            <h2>{s.titulo}</h2>
            {s.pares && (
              <dl className="grade-dados">
                {s.pares.map(([k, x]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{x}</dd>
                  </div>
                ))}
              </dl>
            )}
            {s.tabela && <div className="rolagem">{s.tabela}</div>}
            {s.alerta && (
              <p className="aviso aviso-alerta" data-teste="pendencias-documento">
                {s.alerta}
              </p>
            )}
          </section>
        ))}
      </div>
      <Historico itens={doc.historico} />
      {cancelar && (
        <Modal
          titulo={`Cancelar ${doc.numero}`}
          aoFechar={() => setCancelar(false)}
          rodape={
            <>
              <button type="button" className="botao-secundario" onClick={() => setCancelar(false)}>
                Voltar
              </button>
              <button type="button" className="botao-primario" disabled={!motivo.trim()} onClick={() => void acao(() => api.post(`/api/ordens/${doc.numero}/cancelar`, { motivo: motivo.trim() }))} data-teste="confirmar-cancelar-documento">
                Confirmar cancelamento
              </button>
            </>
          }
        >
          <p className="aviso aviso-info">Ação sensível: o cancelamento é definitivo, com motivo, usuário e data na auditoria. O Pedido não muda.</p>
          <label className="campo">
            Motivo (obrigatório)
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} data-teste="motivo-cancelar-documento" />
          </label>
        </Modal>
      )}
    </>
  );
}

/* =============================== PDFs =============================== */

function DocumentoOperacionalPdf({ doc }: { doc: DocCompleto }) {
  const titulo = doc.tipo === 'locacao' ? 'ORDEM DE LOCAÇÃO AO PARCEIRO' : 'ORDEM DE TRADE-IN AO COMPRADOR';
  return (
    <FolhaDocumento titulo={titulo} subtitulo={`${doc.numero} · ${doc.situacaoTexto} · documento provisório`} empresa={doc.pedidoCompleto.conteudo.empresa ?? null} rascunho={doc.situacao === 'em_conferencia' ? 'EM CONFERÊNCIA — NÃO EMITIDA' : doc.situacao === 'cancelada' ? 'CANCELADA' : undefined} avisoRascunho={doc.situacao === 'em_conferencia' ? 'Em conferência: o documento definitivo é o emitido.' : 'Documento cancelado.'} voltar={`/ordens/${doc.numero}`} teste="pdf-documento-operacional">
      {secoesDocumento(doc).map((s) => (
        <Secao key={s.teste} titulo={s.titulo} teste={`pdf-${s.teste}`}>
          {s.pares && <Campos pares={s.pares} />}
          {s.tabela}
          {s.alerta && <p className="folha-nota">{s.alerta}</p>}
        </Secao>
      ))}
    </FolhaDocumento>
  );
}

/** Rótulo do frete no resumo da OCF (mesmos dados de antes, sem frase redundante no CIF). */
function rotuloFreteOcf(f: { modalidade: string; pagador: string }) {
  if (f.modalidade === 'CIF') return 'Frete (CIF — incluso no preço)';
  return f.pagador === 'cliente' ? 'Frete (FOB — pago pelo cliente)' : 'Frete (FOB — pago pela INTTRIX, descontado da comissão)';
}
const fmtPtax = (x: number) => x.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

/**
 * PDF da OCF — layout aprovado em 08/10/2026: destinado ao fornecedor faturador (mostra custo, venda e margem — nunca
 * vão na Proposta ao Cliente). PTAX: valor e data de fechamento da cotação da versão aceita (sem recálculo); sem os
 * dados de cancelamento no documento.
 */
function OrdemPdf({ o }: { o: OcfCompleta }) {
  const pedido = o.pedidoCompleto;
  const c = pedido.conteudo;
  const e = o.entidade;
  const fat = c.faturamento;
  const ent = c.entrega;
  const empresa = c.empresa ?? null;
  const entregaIgual = ent.origem === 'mesmo endereço do faturamento' || (ent.cnpj === fat.cnpj && mesmoEndereco(ent.endereco, fat.endereco));
  const refPtax = (itemId: string) => o.ptaxReferencias?.[itemId] ?? { valor: c.itens.find((x) => x.itemId === itemId)?.ptax ?? c.ptax, data: null };
  const usd = o.itens.filter((i) => produtoDe(pedido, i.itemId)?.moeda === 'USD');
  const ptaxConsideradas = [...new Map(usd.map((i) => refPtax(i.itemId)).map((r) => [`${r.valor}|${r.data}`, r])).values()];
  const eco = economia(linhasOrdem(o), o.frete);
  const contatoEntrega = [o.contatoEntrega, o.telefoneEntrega].filter(Boolean);
  return (
    <DocumentoInttrix
      titulo="ORDEM DE COMPRA AO FORNECEDOR"
      subtitulo="Documento destinado ao fornecedor faturador."
      colunasIdent={2}
      ident={[
        ['OCF', o.numero],
        ['Data', dataBr(o.dataCriacao)],
        ['Proposta de origem', `${pedido.proposta.numero} | V${pedido.versao}`, true],
      ]}
      empresa={empresa}
      rascunho={!o.emitida && !o.enviada && !o.cancelamento ? 'EM CONFERÊNCIA — NÃO EMITIDA' : o.situacao === 'cancelada' ? 'CANCELADA' : undefined}
      avisoRascunho={!o.emitida && !o.enviada && !o.cancelamento ? 'Em conferência: o documento definitivo é o emitido.' : 'Ordem cancelada.'}
      voltar={`/ordens/${o.numero}`}
      teste="pdf-ordem"
      paginado
    >
      <Bloco n={1} titulo="SELEÇÃO DO FATURADOR" teste="pdf-faturador">
        <div className="d-corpo d-par">
          <Icone nome="predio" grande />
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr', flex: 1 }}>
            {/* só a entidade efetivamente selecionada para faturar esta Ordem e o CNPJ dela (decisão de 09/10/2026) */}
            {/* C16: "Entidade faturadora" nos novos PDFs; a OCF já emitida mantém o rótulo com que foi emitida */}
            <Pares pares={[[o.rotuloEntidade ?? 'Entidade faturada', e.fantasia && e.fantasia !== e.razao ? `${e.razao} (${e.fantasia})` : e.razao], [`CNPJ da ${(o.rotuloEntidade ?? 'Entidade faturada').toLowerCase()}`, e.cnpj, true]]} />
            <Pares pares={[['Proposta de origem', `${pedido.proposta.numero} | V${pedido.versao}`], ['Número da OCF', o.numero, true], ['Condição de pagamento', pagamentoTexto(c.condicoes)], ['Data', dataBr(o.dataCriacao)]]} />
          </div>
        </div>
      </Bloco>
      <Bloco n={2} titulo="DADOS DA INTTRIX" teste="pdf-inttrix">
        <div className="d-corpo d-par">
          <Icone nome="predio" grande />
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr', flex: 1 }}>
            <Pares pares={[['Razão social', empresa?.razao ?? 'a configurar (P-18)'], ['CNPJ', empresa?.cnpj ?? 'a configurar (P-18)'], ['Código da revenda no fornecedor', e.codigoRevenda]]} />
            <Pares pares={[['Responsável', pedido.responsavel.nome], ['E-mail', pedido.responsavel.email], ['Proposta e versão de origem', `${pedido.proposta.numero} | V${pedido.versao}`]]} />
          </div>
        </div>
      </Bloco>
      <Bloco n={3} titulo="CLIENTE FINAL E FATURAMENTO" teste="pdf-cliente-final">
        <div className="d-corpo d-par">
          <Icone nome="pessoa" grande />
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1.15fr 1fr', flex: 1 }}>
            <Pares pares={[['Razão social', fat.razao, true], ['CNPJ', fat.cnpj], ['Grupo / código interno', `${pedido.cliente.nome} (${pedido.cliente.codigo})`], ['Inscrição estadual', textoHistorico(pedido.cadastro.faturamento.ie, pedido.cadastro.dataReferencia)], ['Endereço de faturamento', linhasEndereco(fat.endereco).join(' — ')]]} />
            <Pares
              pares={[
                // P4 bloco 3 (D3): contato escolhido na Proposta (congelado), não o cadastro atual
                ['Contato', fat.contato],
                ['E-mail', textoHistorico(pedido.cadastro.faturamento.email, pedido.cadastro.dataReferencia, false)],
                ['Telefone', fat.telefone],
                ['OC do cliente', pedido.ocsVigentes.map((x) => `${x.numero} (${x.tipo.toLowerCase()})`).join(' · ') || 'pendente — Cliente ainda não enviou'],
              ]}
            />
          </div>
        </div>
      </Bloco>
      <Bloco n={4} titulo="ENTREGA" extra={entregaIgual ? undefined : '(ENDEREÇOS DIFERENTES)'} teste="pdf-entrega">
        {entregaIgual ? (
          <div className="d-corpo d-grade d-div-v" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
            <div className="d-par"><Icone nome="caminhao" grande /><div><div className="d-r d-forte">Entregar no endereço de faturamento</div><Linhas linhas={linhasEndereco(fat.endereco)} /></div></div>
            <div className="d-par"><Icone nome="telefone" grande /><div><div className="d-r d-forte">Contato de entrega</div>{contatoEntrega.length ? <Linhas linhas={contatoEntrega} /> : <div className="d-v">—</div>}</div></div>
          </div>
        ) : (
          <div className="d-corpo d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr .8fr' }}>
            <div className="d-par"><Icone nome="predio" grande /><div><div className="d-r d-forte">Endereço de faturamento</div><div className="d-v">CNPJ {fat.cnpj || '—'}</div><Linhas linhas={linhasEndereco(fat.endereco)} /></div></div>
            <div className="d-par"><Icone nome="caminhao" grande /><div><div className="d-r d-forte">Endereço de entrega</div><div className="d-v">CNPJ {ent.cnpj || '—'}</div><Linhas linhas={linhasEndereco(ent.endereco)} /></div></div>
            <div className="d-par"><Icone nome="telefone" grande /><div><div className="d-r d-forte">Contato de entrega</div>{contatoEntrega.length ? <Linhas linhas={contatoEntrega} /> : <div className="d-v">—</div>}</div></div>
          </div>
        )}
      </Bloco>
      <Bloco n={5} titulo="ITENS DO PEDIDO" teste="pdf-itens" quebra>
        <div className="d-corpo d-tabela">
          <table className="d-tab d-ocf">
            <thead>
              <tr>
                <th>PN/SKU</th>
                <th>Descrição</th>
                <th className="d-centro">Qtd.</th>
                <th>Unid.</th>
                <th>Disponib.</th>
                <th>Moeda</th>
                <th className="d-num">Custo unit.</th>
                <th className="d-num">Custo total</th>
                <th className="d-num">Venda unit.</th>
                <th className="d-num">Venda total</th>
                <th className="d-num">
                  Margem
                </th>
                <th className="d-num">%</th>
              </tr>
            </thead>
            <tbody>
              {o.itens.map((i) => {
                const pr = produtoDe(pedido, i.itemId);
                const margem = (i.precoUnit - i.custoUnit) * i.quantidade;
                const r = pr?.moeda === 'USD' ? refPtax(i.itemId) : null;
                return (
                  <tr key={i.itemId}>
                    <td className="d-pn">{pr?.sku ?? '—'}</td>
                    <td className="d-desc">{pr?.nome ?? i.itemId}</td>
                    <td className="d-centro">{i.quantidade}</td>
                    <td>{pr?.unidade ?? ''}</td>
                    <td>{i.disponibilidade}</td>
                    <td>
                      {pr?.moeda ?? 'BRL'}
                      {r && <span className="d-ptax">PTAX {fmtPtax(r.valor)}</span>}
                      {r?.data && <span className="d-ptax">fechamento em {dataBr(r.data)}</span>}
                    </td>
                    <td className="d-num">
                      {moeda2(i.custoUnit)}
                      {r && <span className="d-ptax">US$ {(i.custoUnit / r.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                    </td>
                    <td className="d-num">{moeda2(i.custoUnit * i.quantidade)}</td>
                    <td className="d-num">{moeda2(i.precoUnit)}</td>
                    <td className="d-num">{moeda2(i.precoUnit * i.quantidade)}</td>
                    <td className="d-num">{moeda2(margem)}</td>
                    <td className="d-num">{pct(i.precoUnit * i.quantidade ? margem / (i.precoUnit * i.quantidade) : 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Bloco>
      <Bloco n={6} titulo="RESUMO FINANCEIRO" teste="pdf-resumo">
        <div className="d-corpo d-resumo">
          <div className="d-col">
            <div className="d-lin"><span>Custo dos produtos</span><b>{moeda2(eco.custo)}</b></div>
            <div className="d-lin"><span>Margem bruta</span><b>{moeda2(eco.bruta)}</b></div>
            <div className="d-lin"><span>{rotuloFreteOcf(eco.frete)}</span><b>{moeda2(eco.frete.valor)}</b></div>
          </div>
          <div className="d-col">
            <div className="d-lin"><span>Venda dos produtos</span><b>{moeda2(eco.venda)}</b></div>
            <div className="d-lin"><span>Margem após frete</span><b>{moeda2(eco.aposFrete)}</b></div>
            {ptaxConsideradas.length > 0 && (
              <div className="d-lin">
                <span>PTAX considerada</span>
                <b>{ptaxConsideradas.map((r) => `${fmtPtax(r.valor)}${r.data ? ` — fechamento em ${dataBr(r.data)}` : ''}`).join(' / ')}</b>
              </div>
            )}
          </div>
          <div className="d-afaturar">
            <span className="d-r">
              VALOR A FATURAR{' '}
              <br />
              AO CLIENTE FINAL
            </span>
            <span className="d-val">{moeda2(eco.aFaturar)}</span>
          </div>
        </div>
      </Bloco>
      <Bloco n={7} titulo="INFORMAÇÕES COMPLEMENTARES" teste="pdf-complementares">
        <div className="d-corpo d-par">
          <Icone nome="nota" grande />
          <div className="d-grade d-div-v" style={{ gridTemplateColumns: '1fr 1fr', flex: 1 }}>
            <Pares pares={[['Modalidade do frete', `${o.frete.modalidade} — ${PAGADORES[o.frete.pagador]}`], ['Situação da Ordem', ESTADOS_ORDEM[o.situacao]], ['Ordem substituta', o.substituta ?? (o.origem ? `esta Ordem substitui a ${o.origem}` : '—')]]} />
            <Pares pares={[['Observações', o.observacoes], ['Data de criação', dataBr(o.dataCriacao)], ['Data de envio', o.enviada ? `${quando(o.enviada.dataHora)} por ${o.enviada.autor}` : '—']]} />
          </div>
        </div>
      </Bloco>
    </DocumentoInttrix>
  );
}
